import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import TransactionTimeline from '../components/transaction/TransactionTimeline';
import TransactionReviewModal from '../components/transaction/TransactionReviewModal';
import TransactionDocuments from '../components/transaction/TransactionDocuments';
import DisclosureTab from '../components/transaction/DisclosureTab';
import TransactionChatParties from '../components/transaction/TransactionChatParties';
import TransactionProperties from '../components/transaction/TransactionProperties';
import SellerOffers from '../components/transaction/SellerOffers';
import TransactionInbox from '../components/transaction/TransactionInbox';
import TransactionHistory from '../components/transaction/TransactionHistory';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getAccessControl, getUserInfo, getUserRoleInTransaction, hasPermissionForRole } from '../utils/accessControl';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { formatUSPhoneInput } from '../utils/phone';
import { ensureStagesLoaded, getCachedStages } from '../services/prospectStageCache';
import { ensureActiveListingStagesLoaded, areActiveListingStagesLoaded } from '../services/activeListingStageCache';
import { ensureActiveBuyingStagesLoaded, areActiveBuyingStagesLoaded } from '../services/activeBuyingStageCache';
import { parseTransactionDocuments } from '../services/ria.service';
import { SERVICE_TYPE_OPTIONS } from '../data/serviceTypes';
import { getServiceTypeLabel } from '../utils/serviceTypeLabel';
import { toUtcWallClock, toUtcInstant, nowWallClockInViewerZone } from '../utils/dateTime';
import './TransactionDetailPage.css';

// Default closure fee shown in the banner for closed transactions without a
// PaymentIntent yet. Keep in sync with rialty-payment-service
// rialty.charge.default-amount-cents and PaymentsPage DEFAULT_FEE_CENTS.
const CLOSURE_FEE_CENTS = 10000;

// Shared Stripe promise. Mirrors PaymentsPage — same publishable key, same
// EmbeddedCheckout flow. Module-scope so the loader fires once per app.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const formatCentsAsUsd = (cents) =>
  '$' + ((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Map the backend's Transaction.paymentStatus (nullable) to the banner's
// visual state. Mirrors the five-state model in PaymentsPage.derivePaymentUiState
// but drops the retry/receipt actions — the banner only needs to decide
// between "due" (show Pay Now that goes to the Payments page) and
// "complete-ish" (just show status, no action).
const derivePaymentBannerState = (paymentStatus, refundedAmountCents) => {
  switch (paymentStatus) {
    case 'PAID':
      return { kind: 'paid', color: '#10B981', bg: 'rgba(16,185,129,0.08)', label: 'Payment completed', showPayNow: false };
    case 'PROCESSING':
      return { kind: 'processing', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: 'Payment processing', showPayNow: false };
    case 'FAILED':
      return { kind: 'failed', color: '#EF4444', bg: 'rgba(239,68,68,0.08)', label: 'Payment failed — retry on the Payments page', showPayNow: true, payLabel: 'Retry' };
    case 'ACTION_REQUIRED':
      return { kind: 'action', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: 'Payment needs action', showPayNow: true, payLabel: 'Complete Payment' };
    case 'REFUNDED':
      return { kind: 'refunded', color: '#64748B', bg: 'rgba(100,116,139,0.08)', label: `Payment refunded${refundedAmountCents ? ' (' + formatCentsAsUsd(refundedAmountCents) + ')' : ''}`, showPayNow: false };
    case 'PARTIALLY_REFUNDED':
      return { kind: 'partially_refunded', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: `Payment partially refunded${refundedAmountCents ? ' (' + formatCentsAsUsd(refundedAmountCents) + ')' : ''}`, showPayNow: false };
    case 'DISPUTED':
      return { kind: 'disputed', color: '#EF4444', bg: 'rgba(239,68,68,0.08)', label: 'Payment disputed by cardholder', showPayNow: false };
    case 'CANCELED':
    case null:
    case undefined:
    default:
      return { kind: 'due', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: `Payment due · ${formatCentsAsUsd(CLOSURE_FEE_CENTS)} closure fee`, showPayNow: true, payLabel: 'Pay Now' };
  }
};

// Matches mobile getTemplateName() — checks financingType for CASH/FHA variants
const getTemplateName = (txn) => {
  const side = txn?.side?.toUpperCase() || '';
  const type = txn?.type?.toUpperCase() || '';
  // Check side first — it's the authoritative transaction-direction field.
  // Fall back to type so both SALE/LISTING variations are covered regardless of field name.
  const isListing = side === 'LISTING' || side === 'SALE' || side === 'SELLING'
    || type === 'LISTING' || type === 'SALE';
  if (isListing) return 'STANDARD_SALE';
  const ft = txn?.financingType?.toLowerCase();
  if (ft === 'cash') return 'CASH_PURCHASE';
  if (ft === 'fha') return 'FHA_PURCHASE';
  return 'STANDARD_PURCHASE';
};

// Internal service-type key -> human label. Module scope so both the page and
// AssignServiceProviderModal (via props) can rely on a stable reference.
const SERVICE_ENTRY_LABELS = {
  inspection: 'Home Inspection',
  appraisal: 'Appraisal',
  repairs: 'Repairs',
  staging: 'Staging',
  photography: 'Photography',
  other: 'Other',
  escrow_company: 'Escrow Company',
  title_company: 'Title Company',
  mortgage_lender: 'Mortgage Lender',
};

// Internal service-type key -> API serviceType query param (mirrors mobile
// SERVICE_TYPE_TO_PROVIDER_TYPE). Module scope for the same reason as above.
const SERVICE_TYPE_TO_API_PARAM = {
  inspection:     'Home Inspection',
  appraisal:      'Appraisal',
  repairs:        'Contractor',
  staging:        'Staging',
  photography:    'Photography',
  other:          'Other',
  escrow_company: 'Escrow',
  title_company:  'Title',
  mortgage_lender:'Mortgage Lender',
};

// Mirrors CreateServiceRequestPage URGENCY_LEVELS so the Add-To-Timeline
// service-request form ships the same enum the CSR flow already sends.
const URGENCY_LEVELS = [
  { value: 'NORMAL',    label: 'Normal' },
  { value: 'URGENT',    label: 'Urgent' },
  { value: 'EMERGENCY', label: 'Emergency' },
];

// Guard for the Phase-4 SP-radius params. Backend expects real Numbers on
// referenceLat/referenceLng — a stringified "37.7749" or a stray NaN would
// serialize into the query string and land in the controller as a broken
// bind (Spring rejects with 400). Returns null for anything non-finite so
// the callsite can fall through cleanly to the requester-coord / state path.
//
// Also rejects exact zero: (0, 0) is the "unset" default for a Java Double
// column that never got stamped — no real US property or agent address
// resolves to lat/lng of exactly 0.0. Without this guard, a transaction
// whose propertyLatitude was never populated would still trigger radius
// mode with `referenceLat=0&referenceLng=0`, which the backend would
// happily filter against — dropping every SP whose coords aren't within
// 25 mi of the Gulf of Guinea.
const finiteOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const TransactionDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'timeline');
  const [pendingAuditChip, setPendingAuditChip] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineEntryType, setTimelineEntryType] = useState('custom');
  const [serviceProviderType, setServiceProviderType] = useState('external');
  const [transaction, setTransaction] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Payment status banner — needs payment methods to decide direct-charge
  // vs redirect-to-add-card on Pay Now click.
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isBannerPaying, setIsBannerPaying] = useState(false);
  const [bannerToast, setBannerToast] = useState(null);
  // Stripe hosted-checkout state for the Pay-Now banner. Same shape as
  // PaymentsPage.handleCreateCharge: fetch a Checkout session clientSecret,
  // mount <EmbeddedCheckout /> in the modal, dispose on close/complete.
  const [showPayNowModal, setShowPayNowModal] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [isMilestonesLoading, setIsMilestonesLoading] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const [templateApplied, setTemplateApplied] = useState(false);
  const [isCreatingProspectStages, setIsCreatingProspectStages] = useState(false);
  const [prospectStagesCreated, setProspectStagesCreated] = useState(false);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [propertiesCount, setPropertiesCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [offerCount, setOfferCount] = useState(0);
  const [transactionHasProperties, setTransactionHasProperties] = useState(false);
  const [firstInterestedPropertyId, setFirstInterestedPropertyId] = useState(null);
  const [calendarForm, setCalendarForm] = useState({
    title: '',
    entryType: '',
    category: '',
    providerType: 'EXTERNAL_VENDOR',
    providerName: '',
    providerPhone: '',
    providerEmail: '',
    serviceType: '',
    // Free-text name when Service Type is "Other" — see CreateServiceRequestPage.
    customServiceType: '',
    scheduledDate: '',
    // Due By Time input was removed from the UI; deadline is end-of-day.
    // Backend DTOs still require the field, so we pin it to 23:59.
    scheduledTime: '23:59',
    duration: 60,
    location: '',
    notes: '',
    estimatedAmount: '',
    urgencyLevel: 'NORMAL',
    externalAcknowledged: true
  });
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const [calendarEntryError, setCalendarEntryError] = useState('');
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
  const [calendarEntries, setCalendarEntries] = useState([]);
  // Calendar-service events for this transaction (Custom entries created from the
  // timeline, plus Calendar-page events carrying this transactionId).
  const [calendarEvents, setCalendarEvents] = useState([]);
  // Service requests tied to this transaction — drives Assign SP / Reassign SP
  // gating on service-typed calendar rows (mirrors mobile srStatusById).
  const [transactionServiceRequests, setTransactionServiceRequests] = useState([]);
  // Loaded-signal separate from array-length — an empty list is a valid
  // steady state (transaction has no SRs yet); the timeline needs to know
  // when the fetch has RESOLVED so the per-row status pill can flip out of
  // its "Syncing…" loading state. Mirrors mobile TimelineTab's sentSrsLoaded.
  const [sentSrsLoaded, setSentSrsLoaded] = useState(false);
  const [providerSource, setProviderSource] = useState('network'); // 'network' | 'other'
  const [selectedProviderId, setSelectedProviderId] = useState(null); // ID of provider picked from the picker modal
  // Provider Picker Modal — replaces the pre-2026-08-31 typeahead search.
  // Opens on "Select Provider" click, fetches the full SP list for the
  // currently chosen Service Type once per open, groups into Companies /
  // Individuals for pick.
  const [showProviderPickerModal, setShowProviderPickerModal] = useState(false);
  const [providerList, setProviderList] = useState([]);
  const [providerListLoading, setProviderListLoading] = useState(false);
  // "Look outside the 25-mi radius" toggle for the calendar-form provider
  // picker (W2). Reset to false whenever the picker re-opens so a stale
  // "show all" doesn't leak across pickers.
  const [calendarExpandedSearch, setCalendarExpandedSearch] = useState(false);
  // Search input state for the picker — mirrors CreateServiceRequestPage's
  // provSearch/debouncedProvSearch pattern. Typing 3+ chars flips the fetch
  // to name-mode: drops coord filter (implicitly expandedSearch) and sends
  // `name=<query>` so the backend runs the state-wide name predicate
  // (UserController.listServiceProviderUsers matches firstName/lastName/
  // businessName/email). Reset on picker close so a stale query doesn't
  // leak across pickers.
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  const [debouncedProviderSearch, setDebouncedProviderSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedProviderSearch(providerSearchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [providerSearchQuery]);
  // Backend `name` filter requires >= 3 chars (see UserController). Below
  // that, ignore and let the coord/state predicate drive the list.
  const providerNameQuery = debouncedProviderSearch.length >= 3 ? debouncedProviderSearch : '';
  // External Vendor "Service Type" catalog — same canonical list as the
  // SERVICE TYPE picker on /invite-someone (GET /api/v1/service-types).
  // Fallback seeds the picker so it's usable offline; overwritten once the
  // response lands.
  const [vendorServiceTypeOptions, setVendorServiceTypeOptions] = useState(SERVICE_TYPE_OPTIONS);
  // Tracks the previous transaction status so the timeline-intro modal only fires
  // when the status transitions PENDING → ACTIVE in the current session, not on
  // every navigation to an already-Active transaction.
  const prevTransactionStatusRef = useRef(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  // Cross-icon "cancel this service request" confirm — replaces window.confirm
  // with the site's own Yes/No modal style. Holds { entryId, isCalendarEvent }
  // while open, null when closed.
  const [cancelServiceConfirm, setCancelServiceConfirm] = useState(null);
  const [showContractDateModal, setShowContractDateModal] = useState(false);
  const [showNoDocsModal, setShowNoDocsModal] = useState(false);
  // Shown when the agent taps Convert to Active before marking the
  // Listing Agreement Signed stage COMPLETED. Replaces a bare
  // window.alert() so the UX matches the rest of the app.
  const [showLaRequiredModal, setShowLaRequiredModal] = useState(false);
  const [pendingConversionStageId, setPendingConversionStageId] = useState(null);
  const [contractDate, setContractDate] = useState('');
  const [showTimelineIntroModal, setShowTimelineIntroModal] = useState(false);
  // Prospect → Active (BUYING + LISTING): parse → review → convert-to-active
  const [showConversionReviewModal, setShowConversionReviewModal] = useState(false);
  const [parsedConversionResult, setParsedConversionResult] = useState(null);
  // Listing-agreement payload captured by TransactionTimeline on the Upload
  // PA stage. Rides on the final convertToActive call so the backend's
  // applyListingAgreementOverrides fires at the same commit that persists
  // the active timeline. LISTING side only — null on buying.
  const [parsedListingAgreement, setParsedListingAgreement] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [convertingStep, setConvertingStep] = useState('');
  // Prospect → Active (LISTING): document picker state — mirrors mobile listingPicker* in TimelineTab.tsx
  const [listingPickerVisible, setListingPickerVisible] = useState(false);
  const [listingPickerDocs, setListingPickerDocs] = useState([]);
  const [listingPickerSelections, setListingPickerSelections] = useState({});
  const [listingPickerNoMoreAck, setListingPickerNoMoreAck] = useState(false);
  const [isLoadingListingPicker, setIsLoadingListingPicker] = useState(false);
  // Select-only mode: the picker opened from the "Upload Purchase Agreement" stage's
  // "Add a Document" button just picks/uploads + tags docs and RETAINS the selection
  // (no parse). The next stage ("Process and generate timelines") Mark Complete re-runs
  // the picker's submit hidden (via submitListingDocPickerRef) to parse + generate.
  const [pickerSelectOnly, setPickerSelectOnly] = useState(false);
  const submitListingDocPickerRef = useRef(null);
  // Bumped after "Add selected" uploads purchase-agreement docs so the Timeline child
  // re-fetches its purchase_agreement folder list and shows the newly-added files.
  const [paRefreshToken, setPaRefreshToken] = useState(0);
  // "Add documents" redesign state (mirrors mobile TimelineTab.tsx). Document
  // type is now a top-level tab; Upload/Choose is an explicit mode; search +
  // two collapsible sources (Transaction Documents /files + Forms, the same
  // sources as the Docs tab's Forms tab). Parse/convert flow is unchanged.
  const [pickerSearch, setPickerSearch] = useState('');
  const [listingPickerFormsDocs, setListingPickerFormsDocs] = useState([]);
  const [pickerFormsLoading, setPickerFormsLoading] = useState(false);
  const [pickerTxnExpanded, setPickerTxnExpanded] = useState(true);
  const [pickerFormsExpanded, setPickerFormsExpanded] = useState(true);

  // Active Listing stage checklist (LISTING side) — mirrors mobile TimelineTab.tsx state.
  // State-aware: CA/CO=5 stages, TX=12, DEFAULT=10; the per-transaction state is read
  // below inside the fetch effect since `transaction` isn't bound yet at this point.
  const [activeListingStagesReady, setActiveListingStagesReady] = useState(false);
  const [activeBuyingStagesReady, setActiveBuyingStagesReady] = useState(false);
  const [isConvertingToActiveListing, setIsConvertingToActiveListing] = useState(false);
  const [activeListingMlsIdInput, setActiveListingMlsIdInput] = useState('');
  const [isSavingMlsId, setIsSavingMlsId] = useState(false);

  // Convert to Closed — explicit status change, separate from completing the
  // Close of Escrow milestone (Purchase and Listing alike)
  const [isConvertingToClosed, setIsConvertingToClosed] = useState(false);

  // Acceptance-date backfill — PATCH /acceptance-date on the Timeline tab's
  // pending-anchor banner.
  const [isSettingAcceptanceDate, setIsSettingAcceptanceDate] = useState(false);

  // Function to get Firebase token
  const getFirebaseToken = () => {
    return new Promise((resolve, reject) => {
      // If user is already available, get token directly
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      // Otherwise wait for auth state to be ready (with timeout)
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  };

  // Fetch the canonical service-type catalog once on mount, same endpoint and
  // shape as InviteSomeonePage/CreateServiceRequestPage's SERVICE TYPE picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.serviceTypes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        if (!cancelled && catalog.length > 0) {
          setVendorServiceTypeOptions(catalog.map(t => ({ id: t.id, name: t.label })));
        }
      } catch (error) {
        console.warn('[TransactionDetail] Failed to fetch service types, using fallback:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load this transaction's calendar-service events (Custom entries created from
  // the timeline, plus Calendar-page events carrying this transactionId) so they
  // auto-place on the timeline alongside service entries.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(`${API_BASE_URL}/api/v1/calendar/events`, {
          params: { transactionId: id },
          headers: { Authorization: `Bearer ${token}` },
        });
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        if (!cancelled) setCalendarEvents(arr);
      } catch (err) {
        if (!cancelled) console.error('Error fetching calendar events:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load service requests tied to this transaction — needed because a calendar
  // event only ever stores a serviceRequestId *link*, never a live status; the
  // SP can accept/decline server-side without the calendar event changing at
  // all, so Assign SP / Reassign SP gating has to come from this list.
  //
  // Also revalidated on tab focus / visibility change so the requester's
  // status pill catches up when they return after the SP has acted — the
  // endpoint has no server-push, and without this the pill freezes at whatever
  // state was current at initial mount (G4 from the SR lifecycle sync audit).
  const loadTransactionServiceRequests = useCallback(async (signal) => {
    if (!id) return;
    try {
      const token = await getFirebaseToken();
      // Mirrors mobile's listMySentServiceRequests(transactionId) — this
      // endpoint can take 20-30s server-side (it decrypts requester PII per
      // SR), which is fine since this fetch is non-blocking background
      // enrichment, not something the page waits on to render. The shared
      // apiClient's default 30s timeout sits right at the edge of that
      // range, so a request that lands on the slow end loses the race and
      // gets aborted client-side (shows as "(canceled)" in devtools) even
      // though the server would have answered a moment later — override
      // with a wider timeout just for this call.
      const res = await apiClient.get(API_ENDPOINTS.sentServiceRequests(id), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      if (signal?.aborted) return;
      const arr = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []);
      setTransactionServiceRequests(arr);
      setSentSrsLoaded(true);
    } catch (err) {
      if (signal?.aborted) return;
      console.error('Error fetching transaction service requests:', err);
      // Mark as loaded on error too — a hung "Syncing…" pill is worse than
      // an empty state; the row will fall back to whatever tier-1/2/3 can
      // resolve locally and the caller can still refresh via the focus
      // listener above.
      setSentSrsLoaded(true);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    const controller = new AbortController();
    loadTransactionServiceRequests(controller.signal);
    // Revalidate on tab focus / return-to-visible. Handles the common case
    // where the SP accepts/declines on their device while the requester's tab
    // sits in the background — without this, the pill stays stuck on the
    // stale state until a hard route change.
    const onFocus = () => loadTransactionServiceRequests();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadTransactionServiceRequests();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      controller.abort();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [id, loadTransactionServiceRequests]);

  // Fetch the user's saved payment methods once so the Pay Now button on
  // the closure fee banner can decide direct-charge vs redirect-to-add-card
  // without waiting on the main transaction load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const res = await apiClient.get(API_ENDPOINTS.paymentMethods, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const data = res?.data;
        setPaymentMethods(Array.isArray(data) ? data : (data?.data || []));
      } catch (e) {
        // Non-fatal — banner Pay Now will fall through to the redirect
        // path when paymentMethods stays empty.
        console.warn('Payment methods lookup failed on txn detail:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opens the Stripe hosted-checkout modal for the pending closure fee.
  // Same flow as PaymentsPage.handleCreateCharge — POST creates a Checkout
  // session, the returned clientSecret powers <EmbeddedCheckout /> inline.
  //
  // Idempotency note: payment-service keys the checkout session by
  // (userId, transactionId, amount) — see ChargeService.java checkout-v2 key.
  // Stripe rejects a reused key if ANY on-wire parameter changed from the
  // first call. transactionName flows into Stripe's line_item description +
  // session metadata (rialtyTransactionName), so it must match whatever
  // PaymentsPage would send for the same transaction. Resolver below mirrors
  // PaymentsPage.getPropertyAddress exactly.
  const handleBannerPayNow = async () => {
    if (isBannerPaying || !transaction) return;
    setIsBannerPaying(true);
    setCheckoutError(null);
    setShowPayNowModal(true);
    try {
      const token = await getFirebaseToken();
      const transactionName =
        transaction.propertyAddress
        || transaction.address
        || transaction.property?.address
        || transaction.title
        || `Transaction #${transaction.id}`;
      const res = await apiClient.post(
        API_ENDPOINTS.paymentCheckoutSession,
        {
          transactionId: transaction.id,
          transactionName,
          returnUrl: `${window.location.origin}/payments?checkout_session={CHECKOUT_SESSION_ID}`,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const secret = res?.data?.data?.clientSecret;
      if (!secret) {
        throw new Error('Checkout session response missing clientSecret');
      }
      setCheckoutClientSecret(secret);
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      if (status === 409 && body.existingPaymentIntentId) {
        // tx-service's one-charge-per-txn guard. Close the modal and show a
        // toast; the webhook-driven refetch will flip the banner to the live
        // state (PENDING / PAID etc.).
        setShowPayNowModal(false);
        setBannerToast('Payment already in flight — refreshing...');
        setTimeout(() => setBannerToast(null), 3200);
        try {
          const token = await getFirebaseToken();
          const refreshed = await apiClient.get(API_ENDPOINTS.transactionById(transaction.id), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (refreshed?.data) setTransaction(refreshed.data);
        } catch (_) {}
      } else {
        setCheckoutError(body.message || 'Could not start checkout. Please try again.');
      }
    } finally {
      setIsBannerPaying(false);
    }
  };

  // Called by <EmbeddedCheckout onComplete>. Clear the client secret +
  // refetch so the banner reflects the webhook-reported status.
  const handleCheckoutComplete = async () => {
    setCheckoutClientSecret(null);
    setShowPayNowModal(false);
    setBannerToast('Payment submitted. Status will update shortly.');
    setTimeout(() => setBannerToast(null), 3200);
    try {
      const token = await getFirebaseToken();
      const refreshed = await apiClient.get(API_ENDPOINTS.transactionById(transaction.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (refreshed?.data) setTransaction(refreshed.data);
    } catch (_) { /* best-effort */ }
  };

  // User closes the modal mid-checkout. Dispose the client secret so the
  // next click starts a fresh session.
  const closePayNowModal = () => {
    setCheckoutClientSecret(null);
    setCheckoutError(null);
    setShowPayNowModal(false);
  };

  // Lookup maps derived from transactionServiceRequests — mirrors mobile's
  // srStatusById / srStatusByProviderEmail. Field names read defensively since
  // no other caller in this codebase consumes sentServiceRequests yet.
  const srStatusById = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const rid = sr.id || sr.requestId || sr.serviceRequestId;
      const status = sr.status || sr.currentStatus;
      if (rid) map[rid] = status;
    });
    return map;
  }, [transactionServiceRequests]);

  // Full SR objects by id — used by the Timeline tab (1) to show service-entry
  // due dates from the SR's own requestedDate/requestedTime (a real UTC
  // instant, always convertible to the viewer's timezone) rather than the
  // linked calendar event's scheduledDate/scheduledTime, which can be an
  // older/unconverted record or have drifted from the SR, and (2) by the
  // Reassign gate to distinguish Row 5 (individual-SP direct decline, no
  // routedByCompanyId) from Row 7 (Company explicitly declined,
  // routedByCompanyId set) — without the full SR shape the client can't
  // tell them apart from the status alone.
  const srById = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const rid = sr.id || sr.requestId || sr.serviceRequestId;
      if (rid) map[rid] = sr;
    });
    return map;
  }, [transactionServiceRequests]);

  // Keyed by `${email}::${serviceType}` — a single Company provider can be
  // routed multiple different serviceTypes on one transaction (e.g., Hvantage
  // gets both an Appraisal and a Mortgage Broker request), and keying by
  // email alone collapses them to whichever SR was indexed last, so
  // View Details / status pill on the wrong-type row shows the other SR.
  const srStatusByProviderEmail = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const email = (sr.providerEmail || sr.provider?.email || '').toLowerCase();
      const type = (sr.serviceType || '').toLowerCase();
      const status = sr.status || sr.currentStatus;
      if (email && type) map[`${email}::${type}`] = status;
    });
    return map;
  }, [transactionServiceRequests]);

  // Full-SR-by-(provider-email, service-type) map — mirrors mobile TimelineTab's
  // tier-2 linkedSr resolution. Web previously stopped after tier-1 back-link +
  // tier-3 schedule fallback, missing rows where the SR was linkable via
  // its provider email (common on rows that pre-date the back-link stamp).
  // Composite key (see comment on srStatusByProviderEmail above).
  const srByProviderEmail = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const email = (sr.providerEmail || sr.provider?.email || '').toLowerCase();
      const type = (sr.serviceType || '').toLowerCase();
      if (email && type) map[`${email}::${type}`] = sr;
    });
    return map;
  }, [transactionServiceRequests]);

  // Report/document attachments the SP uploaded against a service request —
  // surfaced on the agent's Timeline tab under the matching calendar entry.
  const srAttachmentsById = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const rid = sr.id || sr.requestId || sr.serviceRequestId;
      const attachments = Array.isArray(sr.reportAttachments) ? sr.reportAttachments
        : Array.isArray(sr.attachments) ? sr.attachments
        : [];
      if (rid && attachments.length > 0) map[rid] = attachments;
    });
    return map;
  }, [transactionServiceRequests]);

  // Tier-3 fallback keyed by serviceType (mirrors mobile's srByServiceType).
  // Belt-and-suspenders: the serviceRequestId back-link PUT doesn't always
  // persist server-side, and /sent-service-requests returns providerEmail:
  // null on every row (backend gap), so tier-1 (id) and tier-2 (email) both
  // silently fail in practice. serviceType is the one correlation that
  // actually works, so it's the fallback that matters most in production —
  // see the matching comment on srByServiceType in TransactionTimeline.js.
  // First match wins; transactionServiceRequests is newest-first from the
  // backend so this resolves to the currently-active SR for that service.
  // Tier-3 fallback: serviceType → SR. When multiple SRs share the same
  // serviceType on this transaction (e.g., two Mortgage Broker requests),
  // callers must disambiguate on the timeline side or every entry of that
  // service type collapses to the same SR's status. See `srsByServiceType`
  // below for the list-shape callers use when they have a schedule to
  // match against.
  const srByServiceType = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const key = (sr.serviceType || '').toLowerCase();
      if (key && !(key in map)) map[key] = sr;
    });
    return map;
  }, [transactionServiceRequests]);

  // Same source data, but shaped as `serviceType → SR[]` so timeline
  // renderers can pick the SR whose scheduled datetime best matches the
  // event they're rendering. Preserves the newest-first ordering from
  // transactionServiceRequests. Used by TransactionTimeline to prevent
  // two same-serviceType SRs from both taking on the newest SR's status.
  const srsByServiceType = useMemo(() => {
    const map = {};
    transactionServiceRequests.forEach(sr => {
      const key = (sr.serviceType || '').toLowerCase();
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(sr);
    });
    return map;
  }, [transactionServiceRequests]);

  // Helper function to format status
  const formatStatus = (status) => {
    const statusText = {
      'PENDING': 'Prospect',
      'ACTIVE': 'Active',
      'UNDER_CONTRACT': 'Under Contract',
      'CLOSING': 'Closing',
      'CANCELLED': 'Fell Through',
      'CLOSED': 'Closed'
    };
    return statusText[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'PENDING': 'txn-badge-prospect',
      'ACTIVE': 'txn-badge-active',
      'UNDER_CONTRACT': 'txn-badge-under-contract',
      'CLOSING': 'txn-badge-closing',
      'CANCELLED': 'txn-badge-cancelled',
      'CLOSED': 'txn-badge-closed',
    };
    return classes[status] || '';
  };

  // Fetch transaction and milestones data from API
  useEffect(() => {
    const fetchTransactionData = async () => {
      setIsLoading(true);
      setIsMilestonesLoading(true);

      try {
        const firebaseToken = await getFirebaseToken();
        const headers = {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        };

        // DocuSign is COLORADO-ONLY — skip the DocuSign documents fetch for non-CO agents
        // (they use Lone Wolf, no DocuSign documents). The transaction's own state isn't known
        // yet (fetched in this same Promise.all), so gate on the agent's licenseState.
        const _detailAgentState = (() => {
          try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            return (u.licenseState || u.stateCode || u.state || '').toUpperCase();
          } catch { return ''; }
        })();
        const _detailIsColoradoAgent = _detailAgentState === 'CO' || _detailAgentState === '';

        // Fetch transaction, milestones, and calendar entries in parallel (matches mobile flow)
        // Also fetch participants, docusign docs, and interested properties needed for web UI
        const [transactionResponse, milestonesResponse, calendarResult, participantsResult, docsResult, gcsFilesResult, propertiesResult, inboxResult, offersResult] = await Promise.all([
          apiClient.get(API_ENDPOINTS.transactionById(id), { headers }),
          apiClient.get(API_ENDPOINTS.transactionMilestones(id), { headers }).catch(err => {
            console.error('Error fetching milestones:', err);
            return { data: [] };
          }),
          apiClient.get(API_ENDPOINTS.calendarEntries(id), { headers }).catch(err => {
            console.error('Error fetching calendar entries:', err);
            return { data: [] };
          }),
          apiClient.get(API_ENDPOINTS.transactionParticipants(id), { headers }).catch(err => {
            console.error('Error fetching participants:', err);
            return { data: [] };
          }),
          (!_detailIsColoradoAgent
            ? Promise.resolve({ data: [] })
            : getActiveRoleFromStorage() === 'BUYER'
              ? apiClient.get(API_ENDPOINTS.transactionDocusignDocumentsSharedWith(id), { headers })
              : apiClient.get(API_ENDPOINTS.transactionDocusignDocuments(id), { headers })
          ).catch(err => {
            console.error('Error fetching documents count:', err);
            return { data: [] };
          }),
          // Plain uploads on the "Transaction Documents" tab (GCS-backed /files endpoint) are
          // a separate source from the DocuSign docs above — both are summed into documentsCount
          // below so the sidebar count matches what TransactionDocuments.js shows once opened.
          apiClient.get(API_ENDPOINTS.transactionFiles(id), { headers }).catch(err => {
            console.error('Error fetching GCS documents count:', err);
            return { data: [] };
          }),
          apiClient.get(API_ENDPOINTS.interestedProperties(id), { headers }).catch(err => {
            console.error('Error fetching interested properties:', err);
            return { data: [] };
          }),
          apiClient.get(API_ENDPOINTS.inboundEmailByTransaction(id), { headers }).catch(err => {
            console.error('Error fetching inbox count:', err);
            return { data: [] };
          }),
          apiClient.get(API_ENDPOINTS.transactionOffers(id), { headers }).catch(() => ({ data: [] }))
        ]);

        console.log('Transaction fetched:', transactionResponse.data);
        console.log('Milestones fetched:', milestonesResponse.data);

        const txnData = transactionResponse.data;
        setTransaction(txnData);

        // Only pre-load prospect stage definitions for PENDING (Prospect phase) transactions.
        // Active/Closed transactions use /milestones — calling prospects/stages for them
        // is unnecessary and causes a spurious network request the user observes.
        if ((txnData?.status || '').toUpperCase() === 'PENDING') {
          ensureStagesLoaded(txnData?.agentLicenseState || 'CA');
        }

        setMilestones(milestonesResponse.data || []);

        // For listing transactions, propertyId from the transaction response is used as
        // interestedPropertyId for TDS/SPQ wizards (no separate interested-properties list needed)
        const txnSide = (txnData?.side || txnData?.type || '').toUpperCase();
        if ((txnSide === 'LISTING' || txnSide === 'SALE') && txnData?.propertyId) {
          setFirstInterestedPropertyId(txnData.propertyId);
          setTransactionHasProperties(true);
        }

        // Parse participants from response
        const pData = participantsResult.data;
        if (Array.isArray(pData)) {
          setParticipants(pData);
        } else if (pData && Array.isArray(pData.data)) {
          setParticipants(pData.data);
        } else if (pData && Array.isArray(pData.participants)) {
          setParticipants(pData.participants);
        } else if (pData && Array.isArray(pData.content)) {
          setParticipants(pData.content);
        } else {
          setParticipants([]);
        }

        // Parse documents count — handles both regular ({ documents: [] }) and shared-with ({ envelopes: [] }) shapes
        const dData = docsResult.data;
        let count = 0;
        if (dData && Array.isArray(dData.envelopes)) {
          // shared-with response: count all documents across envelopes (exclude summary/certificate)
          count = dData.envelopes.reduce((sum, env) =>
            sum + (env.documents || []).filter(d => d.type !== 'summary' && d.documentId !== 'certificate').length, 0);
        } else if (Array.isArray(dData)) {
          count = dData.length;
        } else if (dData && Array.isArray(dData.data)) {
          count = dData.data.length;
        } else if (dData && Array.isArray(dData.documents)) {
          count = dData.documents.length;
        } else if (dData && Array.isArray(dData.content)) {
          count = dData.content.length;
        }

        // Add GCS-backed plain uploads (matches TransactionDocuments.js fetchGcsDocuments parsing)
        const gcsData = gcsFilesResult.data;
        let gcsCount = 0;
        if (Array.isArray(gcsData)) {
          gcsCount = gcsData.length;
        } else if (gcsData && Array.isArray(gcsData.data)) {
          gcsCount = gcsData.data.length;
        } else if (gcsData && Array.isArray(gcsData.files)) {
          gcsCount = gcsData.files.length;
        } else if (gcsData && Array.isArray(gcsData.documents)) {
          gcsCount = gcsData.documents.length;
        } else if (gcsData && Array.isArray(gcsData.content)) {
          gcsCount = gcsData.content.length;
        }

        setDocumentsCount(count + gcsCount);

        // Parse inbox count from response
        const inboxData = inboxResult.data;
        const inboxEmails = Array.isArray(inboxData) ? inboxData
          : Array.isArray(inboxData?.data) ? inboxData.data
          : [];
        setInboxCount(inboxEmails.length);

        // Parse offers count from response
        const offersData = offersResult.data;
        const offersList = Array.isArray(offersData) ? offersData
          : Array.isArray(offersData?.offers) ? offersData.offers
          : Array.isArray(offersData?.data) ? offersData.data
          : [];
        setOfferCount(offersList.length);

        // Parse calendar entries from response
        const ceData = calendarResult.data;
        if (Array.isArray(ceData)) {
          setCalendarEntries(ceData);
        } else if (ceData && Array.isArray(ceData.data)) {
          setCalendarEntries(ceData.data);
        } else if (ceData && Array.isArray(ceData.content)) {
          setCalendarEntries(ceData.content);
        } else {
          setCalendarEntries([]);
        }

        // Parse interested properties to pre-set firstInterestedPropertyId for RPA Wizard
        const propData = propertiesResult.data;
        let propList = [];
        if (Array.isArray(propData)) propList = propData;
        else if (propData && Array.isArray(propData.data)) propList = propData.data;
        else if (propData && Array.isArray(propData.properties)) propList = propData.properties;
        else if (propData && Array.isArray(propData.results)) propList = propData.results;
        else if (propData && Array.isArray(propData.content)) propList = propData.content;
        if (propList.length > 0) {
          setTransactionHasProperties(true);
          setPropertiesCount(propList.length);
          const firstId = propList[0]?.id || propList[0]?.interestedPropertyId || null;
          setFirstInterestedPropertyId(firstId);
        }


      } catch (error) {
        console.error('Error fetching transaction:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setTransaction(null);
        setMilestones([]);
      } finally {
        setIsLoading(false);
        setIsMilestonesLoading(false);
      }
    };

    if (id) {
      fetchTransactionData();
    }
  }, [id]);

  // Function to apply milestone template
  const applyMilestoneTemplate = useCallback(async (templateName) => {
    try {
      const firebaseToken = await getFirebaseToken();
      console.log('Applying milestone template:', templateName);
      const response = await apiClient.post(
        API_ENDPOINTS.applyMilestoneTemplate(id),
        { templateName },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Milestone template applied successfully:', response.data);
      return response.data;
    } catch (error) {
      // 409 = template already applied, milestones already exist — not a real error
      if (error.response?.status === 409) {
        console.log('Milestone template already applied (409) — milestones exist, will fetch existing.');
        return null;
      }
      // For all other errors, log and return null so callers can still run fetchMilestones()
      // and the page continues to load rather than getting stuck
      console.error('Error applying milestone template:', error.response?.data?.message || error.message);
      return null;
    }
  }, [id]);

  // Complete a milestone
  const handleCompleteMilestone = useCallback(async (milestoneId) => {
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.completeMilestone(id, milestoneId),
        {},
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Milestone completed:', milestoneId);
      // Re-fetch milestones to update UI
      const response = await apiClient.get(
        API_ENDPOINTS.transactionMilestones(id),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const updatedMilestones = response.data || [];
      setMilestones(updatedMilestones);

      // If the last milestone was just completed and all milestones are done,
      // re-fetch the transaction — if it is now CLOSED, move to the Closed tab.
      const DONE_STATUSES = ['COMPLETED', 'DONE', 'SKIPPED', 'WAIVED'];
      const allDone = updatedMilestones.length > 0 &&
        updatedMilestones.every(m => DONE_STATUSES.includes(m.status?.toUpperCase()));
      const sorted = [...updatedMilestones].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      const lastMilestone = sorted[sorted.length - 1];

      if (allDone && lastMilestone?.id === milestoneId) {
        try {
          const txnRes = await apiClient.get(
            API_ENDPOINTS.transactionById(id),
            { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
          );
          const updatedTxn = txnRes.data;
          if (updatedTxn?.status?.toUpperCase() === 'CLOSED') {
            setTransaction(updatedTxn);
            setTimeout(() => navigate('/transactions', { state: { filter: 'closed' }, replace: true }), 1500);
          }
        } catch { /* non-critical — stay on page if transaction re-fetch fails */ }
      }
    } catch (error) {
      console.error('Error completing milestone:', error);
      alert(error.response?.data?.message || 'Failed to complete milestone. Please try again.');
    }
  }, [id, navigate]);

  // Skip a milestone (N/A)
  const handleSkipMilestone = useCallback(async (milestoneId) => {
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.skipMilestone(id, milestoneId),
        {},
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Milestone skipped:', milestoneId);
      // Re-fetch milestones to update UI
      const response = await apiClient.get(
        API_ENDPOINTS.transactionMilestones(id),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setMilestones(response.data || []);
    } catch (error) {
      console.error('Error skipping milestone:', error);
      alert(error.response?.data?.message || 'Failed to skip milestone. Please try again.');
    }
  }, [id]);

  // Fetch milestones (used after applying template)
  const fetchMilestones = useCallback(async () => {
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(
        API_ENDPOINTS.transactionMilestones(id),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Milestones fetched:', response.data);
      setMilestones(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching milestones:', error);
      return [];
    }
  }, [id]);

  // Handle prospect stage complete (PATCH transaction metadata)
  const handleProspectStageComplete = useCallback(async (stageId, confirmedContractDate = undefined) => {
    // ── buyer_rpa_signed (BUYING side): preview + Add More → parse → review → convert-to-active ──
    // Opens the shared document picker so the agent SEES which files are
    // being parsed before firing, and can add more counter offers from
    // device via the picker's Add More affordance. Previously fired parse
    // directly on a heuristic-matched single doc with zero preview; agents
    // couldn't tell what the parser was consuming or extend it. The picker
    // handles the full tri-source fetch (Lone Wolf finalised signings +
    // GCS uploads + DocuSign envelopes, with the same CA-skips-DocuSign +
    // Lone-Wolf-only rules the inline flow used).
    if (stageId === 'buyer_rpa_signed' && confirmedContractDate === undefined) {
      await triggerActiveListingParseFlow(stageId, 'BUYING');
      return;
    }

    // ── seller_rpa_signed (LISTING side): show document picker → AI parse → review → convert-to-active ──
    // Mirrors mobile TimelineTab.tsx lines 1081-1146: fetch DocuSign docs, show picker modal.
    if (stageId === 'seller_rpa_signed') {
      const txnSideUp = (transaction?.side || transaction?.type || '').toUpperCase();
      if (txnSideUp === 'LISTING' || txnSideUp === 'SELLING' || txnSideUp === 'SALE') {
        setPendingConversionStageId(stageId);
        setIsLoadingListingPicker(true);
        try {
          const token = await getFirebaseToken();
          const authHeader = { 'Authorization': `Bearer ${token}` };
          const listResp = await apiClient.get(
            API_ENDPOINTS.transactionDocusignDocuments(id),
            { headers: authHeader }
          );
          const raw = listResp.data;
          let rawDocs = [];
          if (Array.isArray(raw))                rawDocs = raw;
          else if (Array.isArray(raw?.documents)) rawDocs = raw.documents;
          else if (Array.isArray(raw?.data))      rawDocs = raw.data;
          else if (Array.isArray(raw?.content))   rawDocs = raw.content;

          const isDocSigned = (d) => {
            if (d?.isSigned === true) return true;
            const candidates = [d?.userDisplayStatus, d?.status, d?.signerStatus];
            return candidates.some(s => {
              const v = String(s ?? '').toLowerCase();
              return v === 'signed' || v === 'completed' || v === 'complete';
            });
          };

          const docs = rawDocs
            .filter(d => d?.documentId)
            .map(d => ({
              documentId: String(d.documentId),
              name: d.fileName ?? d.name ?? 'document.pdf',
              userDisplayStatus: d.userDisplayStatus,
              source: 'docusign',
              isSigned: isDocSigned(d),
            }));

          if (docs.length === 0) {
            setShowNoDocsModal(true);
            setPendingConversionStageId(null);
            return;
          }

          setListingPickerDocs(docs);
          setListingPickerNoMoreAck(false);
          // Default first doc → RPA, rest unselected (mirrors mobile lines 1126-1135)
          const initial = {};
          const firstDoc = docs[0];
          initial[firstDoc.documentId] = { documentId: firstDoc.documentId, name: firstDoc.name, label: 'RPA', source: firstDoc.source };
          setListingPickerSelections(initial);
          setListingPickerVisible(true);
        } catch (err) {
          const message = err?.response?.data?.message || err?.message || 'Could not load documents.';
          alert(message);
          setPendingConversionStageId(null);
        } finally {
          setIsLoadingListingPicker(false);
        }
        return;
      }
    }

    try {
      const firebaseToken = await getFirebaseToken();
      const now = new Date().toISOString();

      // If metadata has no stages, initialize from cache (mirrors mobile computeUpdatedTimeline).
      // This allows sellers/buyers to complete stages even before an agent has opened the page.
      const txnState = transaction?.agentLicenseState || 'CA';
      let currentTimeline = transaction?.metadata?.prospectTimeline;
      if (!currentTimeline?.stages?.length) {
        await ensureStagesLoaded(txnState);
        const txnSideUpper = (transaction?.side || transaction?.type || '').toUpperCase();
        const isB = txnSideUpper === 'BUYING' || txnSideUpper === 'PURCHASE';
        const defs = getCachedStages(isB ? 'BUYING' : 'LISTING', txnState);
        const initAt = transaction?.createdAt || now;
        currentTimeline = {
          side: isB ? 'BUYING' : 'LISTING',
          startedAt: initAt,
          stages: defs.map((def, i) => ({
            stageId: def.id,
            status: i === 0 ? 'COMPLETED' : 'NOT_STARTED',
            ...(i === 0 ? { completedAt: initAt } : {}),
          })),
        };
      }

      // Upsert: update existing stage or add if not present (matches mobile behavior)
      const existingIndex = currentTimeline.stages.findIndex(s => s.stageId === stageId);
      let updatedStages;
      if (existingIndex >= 0) {
        updatedStages = currentTimeline.stages.map(stage =>
          stage.stageId === stageId
            ? { ...stage, status: 'COMPLETED', completedAt: now }
            : stage
        );
      } else {
        updatedStages = [...currentTimeline.stages, { stageId, status: 'COMPLETED', completedAt: now }];
      }

      // Update currentStageId to next incomplete stage (matches mobile behavior)
      const nextIncomplete = updatedStages.find(
        s => s.status !== 'COMPLETED' && s.status !== 'SKIPPED'
      );
      const updatedTimeline = {
        ...currentTimeline,
        stages: updatedStages,
        ...(nextIncomplete ? { currentStageId: nextIncomplete.stageId } : {})
      };

      const patchPayload = {
        // Save effectiveDate at root level (matches mobile transactionService.updateTransaction)
        ...(confirmedContractDate ? { effectiveDate: confirmedContractDate } : {}),
        metadata: {
          prospectTimeline: updatedTimeline,
          ...(confirmedContractDate ? { contractDate: confirmedContractDate } : {})
        }
      };

      const response = await apiClient.patch(
        API_ENDPOINTS.transactionById(id),
        patchPayload,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Prospect stage completed:', stageId);
      if (response.data) {
        setTransaction(response.data);
      }

      // If this is a conversion trigger stage, change status → ACTIVE then apply template
      // Matches mobile: prospectService.convertToActive() → autoApplyTemplate effect
      if (stageId === 'buyer_rpa_signed' || stageId === 'seller_rpa_signed') {
        try {
          // 1. POST status change to ACTIVE
          await apiClient.post(
            API_ENDPOINTS.changeTransactionStatus(id),
            { status: 'ACTIVE' },
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('Transaction status changed to ACTIVE (conversion trigger)');

          // 2. Re-fetch transaction to get the fully updated active transaction object
          const txnResponse = await apiClient.get(
            API_ENDPOINTS.transactionById(id),
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
          const activeTxn = txnResponse.data || transaction;
          if (txnResponse.data) {
            setTransaction(txnResponse.data);
          }

          // 3. Smart template application — matches mobile TimelineTab stale detection
          setIsApplyingTemplate(true);
          setIsMilestonesLoading(true);
          try {
            const templateName = getTemplateName(activeTxn);

            // Fetch current milestones to determine whether to apply or skip
            const currentMilestones = await fetchMilestones();
            let finalMilestones = currentMilestones;

            if (currentMilestones && currentMilestones.length > 0) {
              // Milestones exist — check if valid (have metadata.roleAccess)
              const hasRoleAccess = currentMilestones.some(m => m.metadata?.roleAccess);
              if (hasRoleAccess) {
                // Valid milestones already exist — skip apply-template (would 409)
                console.log('Valid milestones already exist post-conversion, skipping apply-template');
              } else {
                // Stale milestones (no roleAccess) — delete and re-apply if none are actioned
                const hasActioned = currentMilestones.some(
                  m => m.status === 'COMPLETED' || m.status === 'IN_PROGRESS'
                );
                if (!hasActioned) {
                  console.log('Stale milestones detected post-conversion, deleting and re-applying:', templateName);
                  await Promise.all(
                    currentMilestones.map(m =>
                      apiClient.delete(API_ENDPOINTS.deleteMilestone(id, m.id), {
                        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
                      })
                    )
                  );
                  await applyMilestoneTemplate(templateName);
                  finalMilestones = await fetchMilestones();
                }
                // If actioned milestones exist, leave them as-is
              }
            } else {
              // No milestones — apply template directly
              console.log('Applying template after conversion:', templateName);
              await applyMilestoneTemplate(templateName);
              finalMilestones = await fetchMilestones();
            }
            setTemplateApplied(true);

            // 4. Recalculate milestone due dates based on contract date
            // Matches mobile: recalculateMilestoneDates(contractDate)
            if (confirmedContractDate && finalMilestones.length > 0) {
              try {
                const parseLocalDate = (str) => {
                  const [y, m, d] = str.split('T')[0].split('-').map(Number);
                  return new Date(y, m - 1, d);
                };
                const toLocalDateStr = (d) =>
                  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

                const sorted = [...finalMilestones].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                const baseMilestone = sorted.find(
                  m => m.type === 'CONTRACT_SIGNED' || m.name?.toLowerCase().includes('contract signed')
                ) || sorted[0];

                if (baseMilestone) {
                  const baseDateStr = baseMilestone.dueDate || baseMilestone.createdAt;
                  if (baseDateStr) {
                    const originalBase = parseLocalDate(baseDateStr);
                    const newBase = parseLocalDate(confirmedContractDate);
                    const diffDays = Math.round((newBase - originalBase) / (1000 * 60 * 60 * 24));

                    if (diffDays !== 0) {
                      await Promise.all(
                        sorted.map(async (m) => {
                          const mDateStr = m.dueDate || m.createdAt;
                          if (!mDateStr) return;
                          const oldDate = parseLocalDate(mDateStr);
                          const newDate = new Date(oldDate.getFullYear(), oldDate.getMonth(), oldDate.getDate() + diffDays);
                          try {
                            await apiClient.patch(
                              API_ENDPOINTS.updateMilestone(id, m.id),
                              { dueDate: toLocalDateStr(newDate) },
                              { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
                            );
                          } catch (err) {
                            console.error(`Failed to update due date for milestone ${m.name}:`, err);
                          }
                        })
                      );
                      console.log(`Recalculated ${sorted.length} milestone dates by ${diffDays} days`);
                      finalMilestones = await fetchMilestones();
                    }
                  }
                }
              } catch (err) {
                console.error('Error recalculating milestone dates:', err);
              }
            }

            // 5. Auto-complete "Contract Signed" milestone
            // Matches mobile: transactionService.completeMilestone(transactionId, contractSignedMs.id, ...)
            const contractSignedMs = finalMilestones.find(
              m => m.type === 'CONTRACT_SIGNED' || m.name?.toLowerCase().includes('contract signed')
            );
            if (contractSignedMs && contractSignedMs.status !== 'COMPLETED') {
              try {
                await apiClient.post(
                  API_ENDPOINTS.completeMilestone(id, contractSignedMs.id),
                  {},
                  { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
                );
                console.log('Auto-completed Contract Signed milestone');
                await fetchMilestones();
              } catch (err) {
                console.error('Error auto-completing Contract Signed milestone:', err);
              }
            }
          } finally {
            setIsApplyingTemplate(false);
            setIsMilestonesLoading(false);
          }
        } catch (statusErr) {
          console.error('Error during PENDING→ACTIVE conversion:', statusErr);
        }
      }
    } catch (error) {
      console.error('Error completing prospect stage:', error);
      alert(error.response?.data?.message || 'Failed to complete stage. Please try again.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, transaction, applyMilestoneTemplate, fetchMilestones]);
  // triggerActiveListingParseFlow is defined after this callback (same file,
  // ~700 lines down). Including it in deps would trigger a TDZ ReferenceError
  // on first render — JS evaluates the array literal at useCallback-call time.
  // Omitted deliberately; both callbacks only depend on `id` which is stable
  // for the component's lifetime, so stale-closure is a non-issue here.

  // Handle prospect stage skip (PATCH transaction metadata)
  const handleProspectStageSkip = useCallback(async (stageId) => {
    try {
      const firebaseToken = await getFirebaseToken();
      const now = new Date().toISOString();

      // If metadata has no stages, initialize from cache (mirrors mobile computeUpdatedTimeline)
      const txnState = transaction?.agentLicenseState || 'CA';
      let currentTimeline = transaction?.metadata?.prospectTimeline;
      if (!currentTimeline?.stages?.length) {
        await ensureStagesLoaded(txnState);
        const txnSideUpper = (transaction?.side || transaction?.type || '').toUpperCase();
        const isB = txnSideUpper === 'BUYING' || txnSideUpper === 'PURCHASE';
        const defs = getCachedStages(isB ? 'BUYING' : 'LISTING', txnState);
        const initAt = transaction?.createdAt || now;
        currentTimeline = {
          side: isB ? 'BUYING' : 'LISTING',
          startedAt: initAt,
          stages: defs.map((def, i) => ({
            stageId: def.id,
            status: i === 0 ? 'COMPLETED' : 'NOT_STARTED',
            ...(i === 0 ? { completedAt: initAt } : {}),
          })),
        };
      }

      // Upsert: update existing stage or add if not present (matches mobile behavior)
      const existingIndex = currentTimeline.stages.findIndex(s => s.stageId === stageId);
      let updatedStages;
      if (existingIndex >= 0) {
        updatedStages = currentTimeline.stages.map(stage =>
          stage.stageId === stageId
            ? { ...stage, status: 'SKIPPED', completedAt: now }
            : stage
        );
      } else {
        updatedStages = [...currentTimeline.stages, { stageId, status: 'SKIPPED', completedAt: now }];
      }

      // Update currentStageId to next incomplete stage (matches mobile behavior)
      const nextIncomplete = updatedStages.find(
        s => s.status !== 'COMPLETED' && s.status !== 'SKIPPED'
      );
      const updatedTimeline = {
        ...currentTimeline,
        stages: updatedStages,
        ...(nextIncomplete ? { currentStageId: nextIncomplete.stageId } : {})
      };

      const patchPayload = {
        metadata: {
          prospectTimeline: updatedTimeline
        }
      };

      const response = await apiClient.patch(
        API_ENDPOINTS.transactionById(id),
        patchPayload,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Prospect stage skipped:', stageId);
      if (response.data) {
        setTransaction(response.data);
      }
    } catch (error) {
      console.error('Error skipping prospect stage:', error);
      alert(error.response?.data?.message || 'Failed to skip stage. Please try again.');
    }
  }, [id, transaction]);

  // ── Active Listing 5-stage checklist (LISTING side) ─────────────────────────
  // Mirrors mobile prospect.service.ts: updateActiveListingStageProgress /
  // completeActiveListingStage / skipActiveListingStage / startActiveListing,
  // and TimelineTab.tsx's handleStartActiveListing / handleActiveListingStage*.

  // Load the stage template once this transaction has an activeListingTimeline.
  // Keyed on agentLicenseState so TX agents don't render snake_case ids when the
  // CA fallback is cached first.
  useEffect(() => {
    if (!transaction?.metadata?.activeListingTimeline) return;
    const txnState = transaction?.agentLicenseState || 'CA';
    if (areActiveListingStagesLoaded(txnState)) {
      setActiveListingStagesReady(true);
      return;
    }
    let cancelled = false;
    ensureActiveListingStagesLoaded(txnState).then(() => { if (!cancelled) setActiveListingStagesReady(true); });
    return () => { cancelled = true; };
  }, [transaction?.metadata?.activeListingTimeline, transaction?.agentLicenseState]);

  // Active Buying cache — state-aware: TX / DEFAULT buyer transactions carry
  // metadata.activeBuyingTimeline after convertToActive seeds it on the backend.
  // CA / CO buyer transactions never carry this key (milestone-driven), so this
  // effect stays dormant for them.
  useEffect(() => {
    if (!transaction?.metadata?.activeBuyingTimeline) return;
    const state = (transaction?.agentLicenseState || '').trim().toUpperCase() || 'CA';
    if (areActiveBuyingStagesLoaded(state)) {
      setActiveBuyingStagesReady(true);
      return;
    }
    let cancelled = false;
    ensureActiveBuyingStagesLoaded(state).then(() => { if (!cancelled) setActiveBuyingStagesReady(true); });
    return () => { cancelled = true; };
  }, [transaction?.metadata?.activeBuyingTimeline, transaction?.agentLicenseState]);

  // Keep the inline MLS-ID input in sync with the transaction's saved value.
  useEffect(() => {
    setActiveListingMlsIdInput(transaction?.mlsId ?? '');
  }, [transaction?.mlsId]);

  // Kick off the Active Listing timeline — backend seeds metadata.activeListingTimeline.
  const handleStartActiveListing = useCallback(async () => {
    if (!transaction || isConvertingToActiveListing) return;
    // Gate: the Listing Agreement Signed stage must be marked COMPLETED
    // before conversion. Active Listing's terminal "Process and generate
    // timelines" stage parses that doc, and the backend's convertToActive
    // mandates parsedListingAgreement — catching the gap here surfaces a
    // friendly nudge instead of letting the agent walk through the entire
    // Active Listing checklist only to be rejected.
    const stages = transaction?.metadata?.prospectTimeline?.stages || [];
    const laStage = stages.find((s) => s?.stageId === 'seller_listing_signed');
    if (laStage && laStage.status !== 'COMPLETED' && laStage.status !== 'SKIPPED') {
      setShowLaRequiredModal(true);
      return;
    }
    setIsConvertingToActiveListing(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.startActiveListing(id),
        {},
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      const response = await apiClient.get(
        API_ENDPOINTS.transactionById(id),
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      if (response.data) setTransaction(response.data);
    } catch (error) {
      console.error('Error starting Active Listing timeline:', error);
      alert(error.response?.data?.message || 'Failed to start the Active Listing timeline. Please try again.');
    } finally {
      setIsConvertingToActiveListing(false);
    }
  }, [id, transaction, isConvertingToActiveListing]);

  // Convert transaction to Closed — explicit status change, kept separate from
  // completing the Close of Escrow milestone (marking that milestone complete
  // only completes the milestone; it does not flip the transaction status).
  const handleConvertToClosed = useCallback(async () => {
    if (!transaction || isConvertingToClosed) return;
    setIsConvertingToClosed(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.changeTransactionStatus(id),
        { status: 'CLOSED' },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      const response = await apiClient.get(
        API_ENDPOINTS.transactionById(id),
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      if (response.data) setTransaction(response.data);
      setTimeout(() => navigate('/transactions', { state: { filter: 'closed' }, replace: true }), 1500);
    } catch (error) {
      console.error('Error converting transaction to Closed:', error);
      alert(error.response?.data?.message || 'Failed to convert transaction to Closed. Please try again.');
    } finally {
      setIsConvertingToClosed(false);
    }
  }, [id, transaction, isConvertingToClosed, navigate]);

  // Backfill the acceptance anchor from the Timeline tab's pending-anchor
  // banner. PATCH /acceptance-date — backend recomputes every milestone
  // carrying metadata.anchorPending and auto-completes ACCEPTANCE. Mirrors
  // mobile handleSetAcceptanceDate.
  const handleSetAcceptanceDate = useCallback(async (yyyyMmDd) => {
    if (!transaction || !yyyyMmDd || isSettingAcceptanceDate) return;
    setIsSettingAcceptanceDate(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      await apiClient.patch(
        API_ENDPOINTS.setTransactionAcceptanceDate(id),
        { acceptanceDate: yyyyMmDd },
        { headers }
      );
      // Refetch transaction + milestones so banner disappears and chips flip to real dates.
      const [txnResp] = await Promise.all([
        apiClient.get(API_ENDPOINTS.transactionById(id), { headers }),
        fetchMilestones(),
      ]);
      if (txnResp.data) setTransaction(txnResp.data);
    } catch (error) {
      console.error('Error setting acceptance date:', error);
      alert(error.response?.data?.message || 'Could not set acceptance date. Please try again.');
    } finally {
      setIsSettingAcceptanceDate(false);
    }
  }, [id, transaction, isSettingAcceptanceDate, fetchMilestones]);

  // Merge a stage's new status into metadata.activeListingTimeline.stages[] and persist.
  // Mirrors mobile prospect.service.ts updateActiveListingStageProgress.
  const updateActiveListingStageProgress = useCallback(async (stageId, status) => {
    const firebaseToken = await getFirebaseToken();
    const headers = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
    const existing = transaction?.metadata?.activeListingTimeline;
    const existingStages = Array.isArray(existing?.stages) ? existing.stages : [];
    const now = new Date().toISOString();
    const stageProgress = { stageId, status, completedAt: now };

    const nextStages = existingStages.some(s => s.stageId === stageId)
      ? existingStages.map(s => (s.stageId === stageId ? { ...s, ...stageProgress } : s))
      : [...existingStages, stageProgress];

    const nextIncomplete = nextStages.find(s => s.status !== 'COMPLETED' && s.status !== 'SKIPPED');
    const updatedTimeline = {
      side: existing?.side ?? 'LISTING',
      startedAt: existing?.startedAt,
      stages: nextStages,
      ...(nextIncomplete ? { currentStageId: nextIncomplete.stageId } : {}),
    };

    await apiClient.patch(
      API_ENDPOINTS.transactionById(id),
      { metadata: { ...transaction?.metadata, activeListingTimeline: updatedTimeline } },
      { headers }
    );
    // Mirrors mobile's fetchTransaction(false) after completeActiveListingStage/
    // skipActiveListingStage: re-read the transaction from the backend rather than
    // rendering off the PATCH response body, so the displayed stage order always
    // matches exactly what the API persisted.
    const refreshed = await apiClient.get(API_ENDPOINTS.transactionById(id), { headers });
    if (refreshed.data) setTransaction(refreshed.data);
    return updatedTimeline;
  }, [id, transaction]);

  // Which side the picker is parsing for — set each time the flow opens.
  // Previously hardcoded 'LISTING' at the parse call; now carried in state so
  // the buyer_rpa_signed Mark As can reuse the same preview-then-parse UX
  // (lists files + Add More affordance) via the shared picker.
  const [pickerParseSide, setPickerParseSide] = useState('LISTING');
  // Hidden file input the picker's Upload Document button triggers to add
  // local files to the doc list. Mirrors mobile handleAddListingPickerDocument.
  const pickerUploadInputRef = useRef(null);

  /**
   * Add Document handler for the doc picker. Reads the selected file into
   * a Blob kept on the row (source=local) so submitListingDocPicker can
   * attach it directly without going through a download endpoint. Mirrors
   * mobile handleAddListingPickerDocument (which uses keepLocalCopy to
   * persist the iOS security-scoped tmp URI).
   */
  const handlePickerFileSelected = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-picking the same file
    const fileName = file.name || `document-${Date.now()}.pdf`;
    // PDF only — the conversion parser reads contract PDFs (RPA / CBS /
    // counters). The <input accept> is a hint on some browsers, so guard here.
    const isPdf = (file.type || '').toLowerCase() === 'application/pdf'
      || fileName.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      alert('Please select a PDF document. Images and other file formats are not supported here.');
      return;
    }
    const syntheticId = `local-${Date.now()}`;
    const row = {
      documentId: syntheticId,
      name: fileName,
      source: 'local',
      isSigned: true, // agent-added — trust it as evidence (same as mobile row)
      localBlob: file,
      mimeType: file.type || 'application/pdf',
    };
    setListingPickerDocs(prev => [...prev, row]);
    setListingPickerSelections(prev => {
      // Uploaded file is auto-selected: first → Purchase Agreement, rest →
      // Counter Offer. The per-row type chip lets the agent change it.
      const hasRpa = Object.values(prev).some(s => s.label === 'RPA');
      return {
        ...prev,
        [syntheticId]: {
          documentId: syntheticId,
          name: row.name,
          label: hasRpa ? 'COUNTER_OFFER' : 'RPA',
          source: 'local',
          localBlob: file,
          mimeType: row.mimeType,
        },
      };
    });
  }, []);

  // All-source doc listing → opens the shared document picker, pre-selecting
  // the first doc as RPA. Pulls from (in authority order) finalised Lone Wolf
  // signings, GCS uploads, and DocuSign envelopes. CA transactions skip the
  // DocuSign leg (they don't use DocuSign for the RPA); Lone Wolf transactions
  // only pull signings when esignatureProvider === 'LONEWOLF'. Used by both
  // the listing-side terminal stage AND the buyer_rpa_signed Mark As.
  const triggerActiveListingParseFlow = useCallback(async (stageIdForFallback, sideOverride = 'LISTING', selectOnly = false) => {
    setPendingConversionStageId(stageIdForFallback);
    setPickerParseSide(sideOverride);
    // Parse mode by default; only the "Add a Document" opener passes selectOnly=true.
    // Resetting here guards the buying-side (buyer_rpa_signed) + process flows from a
    // stale select-only value left over from a prior Upload-Purchase-Agreement open.
    setPickerSelectOnly(selectOnly);
    setIsLoadingListingPicker(true);
    try {
      const token = await getFirebaseToken();
      const authHeader = { 'Authorization': `Bearer ${token}` };
      const normalizeDocs = (raw) => {
        if (Array.isArray(raw))                   return raw;
        if (Array.isArray(raw?.data))              return raw.data;
        if (Array.isArray(raw?.documents))          return raw.documents;
        if (Array.isArray(raw?.files))              return raw.files;
        if (Array.isArray(raw?.content))            return raw.content;
        return [];
      };
      // CA skips DocuSign for the RPA — a leftover DocuSign draft shouldn't
      // show up over the signed upload in the picker. Same rule the inline
      // buyer_rpa_signed branch applied before the picker refactor.
      // DocuSign is COLORADO-ONLY — skip the DocuSign envelope fetch for every non-CO state
      // (CA, TX, …). Was `=== 'CA'` (CA only), which wrongly fetched DocuSign for TX.
      const skipDocuSign = (transaction?.agentLicenseState || '').toUpperCase() !== 'CO';
      // Transaction Documents section mirrors the Docs tab's Transaction Documents
      // tab: GCS uploads (+ DocuSign for non-CA). LoneWolf finalised signings are
      // signed FORMS — they belong in the Forms section (fetchPickerForms), not
      // here — so the LoneWolf leg is intentionally NOT fetched in this listing.
      const [dsListResult, gcsListResult] = await Promise.allSettled([
        skipDocuSign
          ? Promise.resolve({ data: [] })
          : apiClient.get(API_ENDPOINTS.transactionDocusignDocuments(id), { headers: authHeader }),
        apiClient.get(API_ENDPOINTS.transactionFiles(id), { headers: authHeader }),
      ]);
      const dsDocs = dsListResult.status === 'fulfilled'
        ? normalizeDocs(dsListResult.value.data).filter(d => d?.documentId).map(d => ({
            documentId: String(d.documentId),
            name: d.fileName ?? d.name ?? 'document.pdf',
            source: 'docusign',
            // Everything attached to this transaction was uploaded deliberately
            // by the agent — treat as signed evidence (mirrors mobile
            // triggerListingParseFlow's isSigned: true on every GCS doc).
            isSigned: true,
          }))
        : [];
      const gcsDocs = gcsListResult.status === 'fulfilled'
        ? normalizeDocs(gcsListResult.value.data)
            // Only PDFs belong in the conversion picker — the parser reads
            // contract PDFs (RPA / CBS / counters). Images and other formats
            // (png/jpg/doc/xls) in the transaction's GCS folder must not
            // surface here, so they can't be picked for parsing.
            .filter(d => {
              if (!(d?.id || d?.documentId)) return false;
              const nm = String(d.fileName ?? d.name ?? '').toLowerCase();
              const ft = String(d.fileType ?? d.contentType ?? d.mimeType ?? '').toLowerCase();
              return nm.endsWith('.pdf') || ft === 'application/pdf';
            })
            .map(d => ({
              documentId: String(d.documentId || d.id),
              name: d.fileName ?? d.name ?? 'document.pdf',
              source: 'gcs',
              isSigned: true,
            }))
        : [];
      // GCS uploads + DocuSign (non-CA) only. LoneWolf finalised signings are
      // signed forms and surface in the Forms section (fetchPickerForms), not here.
      const docs = [...gcsDocs, ...dsDocs];

      // Open the "Add documents" picker regardless of whether GCS docs exist —
      // the modal offers Upload + a Forms section, so an empty /files list no
      // longer dead-ends the flow. Forms + new UI state are loaded by the
      // picker-open effect.
      setListingPickerDocs(docs);
      setListingPickerNoMoreAck(false);
      const initial = {};
      if (docs.length > 0) {
        const firstDoc = docs[0];
        initial[firstDoc.documentId] = { documentId: firstDoc.documentId, name: firstDoc.name, label: 'RPA', source: firstDoc.source };
      }
      setListingPickerSelections(initial);
      setListingPickerVisible(true);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Could not load documents.';
      alert(message);
      setPendingConversionStageId(null);
    } finally {
      setIsLoadingListingPicker(false);
    }
  }, [id]);

  const handleActiveListingStageComplete = useCallback(async (stageId) => {
    if (!transaction) return;

    if (stageId === 'process_and_generate_timelines') {
      // Hidden parse: docs were already selected + tagged in the "Upload Purchase
      // Agreement" stage's picker (select-only). Re-run that picker's submit directly —
      // no modal shown — so it uploads/downloads + parses + generates the timeline.
      const saved = transaction?.metadata?.purchaseAgreementSelection;
      if (!Array.isArray(saved) || saved.length === 0) {
        alert('Add the Purchase Agreement (and any counter offers) in the "Upload Purchase Agreement" step first.');
        return;
      }
      setPickerParseSide('LISTING');
      setPendingConversionStageId(stageId);
      await submitListingDocPickerRef.current?.(saved);
      return;
    }

    if (stageId === 'add_mls_id') {
      const mls = activeListingMlsIdInput.trim();
      if (!mls) {
        alert('Enter an MLS ID before marking this step complete, or use N/A to skip.');
        return;
      }
      setIsSavingMlsId(true);
      try {
        const firebaseToken = await getFirebaseToken();
        const headers = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
        const existing = transaction?.metadata?.activeListingTimeline;
        const existingStages = Array.isArray(existing?.stages) ? existing.stages : [];
        const now = new Date().toISOString();
        const nextStages = existingStages.some(s => s.stageId === stageId)
          ? existingStages.map(s => (s.stageId === stageId ? { ...s, status: 'COMPLETED', completedAt: now } : s))
          : [...existingStages, { stageId, status: 'COMPLETED', completedAt: now }];
        const nextIncomplete = nextStages.find(s => s.status !== 'COMPLETED' && s.status !== 'SKIPPED');
        const updatedTimeline = {
          side: existing?.side ?? 'LISTING',
          startedAt: existing?.startedAt,
          stages: nextStages,
          ...(nextIncomplete ? { currentStageId: nextIncomplete.stageId } : {}),
        };
        await apiClient.patch(
          API_ENDPOINTS.transactionById(id),
          { mlsId: mls, metadata: { ...transaction.metadata, activeListingTimeline: updatedTimeline } },
          { headers }
        );
        // Re-read from the backend rather than trusting the PATCH response body —
        // mirrors mobile's fetchTransaction(false) after this same write.
        const refreshed = await apiClient.get(API_ENDPOINTS.transactionById(id), { headers });
        if (refreshed.data) setTransaction(refreshed.data);
      } catch (err) {
        alert(err.response?.data?.message || 'Could not save MLS ID. Please try again.');
      } finally {
        setIsSavingMlsId(false);
      }
      return;
    }

    try {
      await updateActiveListingStageProgress(stageId, 'COMPLETED');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not update stage. Please try again.');
    }
  }, [transaction, id, activeListingMlsIdInput, listingPickerSelections, updateActiveListingStageProgress]);

  const handleActiveListingStageSkip = useCallback(async (stageId) => {
    if (!transaction) return;
    try {
      await updateActiveListingStageProgress(stageId, 'SKIPPED');
    } catch (err) {
      alert(err.response?.data?.message || 'Could not update stage. Please try again.');
    }
  }, [transaction, updateActiveListingStageProgress]);

  // Confirm contract date and proceed with conversion (called from modal)
  const handleConfirmContractDate = useCallback(async () => {
    const stageId = pendingConversionStageId;
    const date = contractDate;
    setShowContractDateModal(false);
    setPendingConversionStageId(null);
    if (!stageId) return;
    // Active Listing terminal stage never touches metadata.prospectTimeline — route
    // it to the activeListingTimeline updater instead of the generic prospect-stage
    // completion path (which would otherwise write into the wrong metadata key).
    if (stageId === 'process_and_generate_timelines') {
      try {
        await updateActiveListingStageProgress(stageId, 'COMPLETED');
      } catch (err) {
        alert(err.response?.data?.message || 'Could not update stage. Please try again.');
      }
      return;
    }
    await handleProspectStageComplete(stageId, date);
  }, [pendingConversionStageId, contractDate, handleProspectStageComplete, updateActiveListingStageProgress]);

  // Called when agent clicks "Reviewed" in the conversion review modal (BUYING side).
  // Mirrors mobile onReviewed callback in TimelineTab.tsx: POST convert-to-active
  // then fetchTransaction(false) to refresh without a loading spinner.
  const handleConversionReviewed = useCallback(async (editedTimeline) => {
    if (!parsedConversionResult) return;
    setShowConversionReviewModal(false);
    setIsConverting(true);
    setConvertingStep('Saving timeline...'); // mirrors mobile convertingStep message
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      // Re-classify parties by side+role before sending — mirrors mobile classifyForSide
      const txnSideForClassify = (transaction?.side || transaction?.type || '').toUpperCase();
      const LISTING_ACTIVE_ROLES = new Set(['SELLER', 'CO_SELLER', 'SELLER_AGENT', 'SELLERS_AGENT', 'LISTING_AGENT', 'CO_LISTING_AGENT', 'LISTING_BROKERAGE', 'SELLER_BROKERAGE', 'SELLERS_BROKERAGE', 'LISTING_BROKER', 'SELLER_BROKER', 'SELLERS_BROKER']);
      const BUYING_ACTIVE_ROLES  = new Set(['BUYER', 'CO_BUYER', 'BUYER_AGENT', 'BUYERS_AGENT', 'CO_BUYERS_AGENT', 'BUYING_BROKERAGE', 'BUYER_BROKERAGE', 'BUYERS_BROKERAGE', 'BUYING_BROKER', 'BUYER_BROKER', 'BUYERS_BROKER']);
      const isListingSide = txnSideForClassify === 'LISTING' || txnSideForClassify === 'SELLING' || txnSideForClassify === 'SALE';
      const activeRoles = isListingSide ? LISTING_ACTIVE_ROLES : BUYING_ACTIVE_ROLES;
      const classifiedParties = (parsedConversionResult.parties ?? []).map(p => ({
        ...p,
        classification: activeRoles.has((p?.role || '').toUpperCase()) ? 'ACTIVE' : 'REFERENCE',
      }));
      // POST convert-to-active — mirrors mobile prospectService.convertToActiveWithReviewedTimeline
      await apiClient.post(
        API_ENDPOINTS.convertToActive(id),
        {
          // Use the agent's review-screen edits (dates + action owners) when provided.
          timeline: Array.isArray(editedTimeline) ? editedTimeline : parsedConversionResult.timeline,
          parties: classifiedParties,
          propertyAddress: parsedConversionResult.propertyAddress ?? null,
          // Parser-resolved acceptance anchor (#4c). Rides on the request
          // so the resolver chain has a parser-sourced fallback before
          // hitting the "manual entry required" error.
          acceptanceDate: parsedConversionResult.parsedAcceptanceDate ?? null,
          // Carry the parsed listing-agreement payload (if any) so the
          // backend applies listPrice / commission / listingDate / metadata
          // overrides at the same commit that persists the active timeline.
          parsedListingAgreement: parsedListingAgreement ?? null,
        },
        { headers }
      );
      setParsedConversionResult(null);
      setPendingConversionStageId(null);
      // Mark milestones as AI-parsed so autoApplyTemplate never deletes them.
      // Persisted to localStorage so the guard also works on future page loads.
      setTemplateApplied(true);
      try { localStorage.setItem(`txn_ai_timeline_${id}`, '1'); } catch { /* ignore quota errors */ }
      // Refresh transaction + milestones — mirrors mobile fetchTransaction(false)
      const [txnResp] = await Promise.all([
        apiClient.get(API_ENDPOINTS.transactionById(id), { headers }),
        fetchMilestones(),
      ]);
      if (txnResp.data) setTransaction(txnResp.data);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to convert to Active';
      alert(`Could not convert: ${message}`);
      setShowConversionReviewModal(true); // re-open so agent can retry without losing parsed result
    } finally {
      setIsConverting(false);
      setConvertingStep('');
    }
  }, [id, parsedConversionResult, fetchMilestones]);

  // ── Listing picker helpers — mirrors mobile toggleListingDocSelection / setListingDocLabel / submitListingDocPicker ──

  const toggleListingDocSelection = useCallback((doc) => {
    setListingPickerSelections(prev => {
      const next = { ...prev };
      if (next[doc.documentId]) {
        delete next[doc.documentId];
        return next;
      }
      // First checked doc defaults to Purchase Agreement, the rest to Counter
      // Offer. The per-row type chip lets the agent change any doc's type.
      const hasRpa = Object.values(prev).some(s => s.label === 'RPA');
      next[doc.documentId] = {
        documentId: doc.documentId,
        name: doc.name,
        label: hasRpa ? 'COUNTER_OFFER' : 'RPA',
        source: doc.source,
        formKind: doc.formKind,
        formRef: doc.formRef,
      };
      return next;
    });
  }, []);

  const setListingDocLabel = useCallback((documentId, label) => {
    setListingPickerSelections(prev => {
      const next = { ...prev };
      if (!next[documentId]) return prev;
      // Enforce exactly-one-RPA: demote any existing RPA to COUNTER_OFFER
      if (label === 'RPA') {
        Object.keys(next).forEach(k => { if (next[k].label === 'RPA') next[k] = { ...next[k], label: 'COUNTER_OFFER' }; });
      }
      next[documentId] = { ...next[documentId], label };
      return next;
    });
  }, []);

  // Fetch the transaction's Forms for the "Add documents" picker's Forms
  // section — SAME sources as the Docs tab's Forms tab (CO → FormService,
  // CA → DocuSign Rooms, Lone-Wolf-pinned → Lone Wolf forms). Rows are tagged
  // source:'form' with a formKind/formRef describing the download endpoint used
  // at submit time. Best-effort: any source failure yields [].
  const fetchPickerForms = useCallback(async () => {
    const isCO = (transaction?.propertyState || transaction?.agentLicenseState || '').toUpperCase() === 'CO';
    const isLW = transaction?.esignatureProvider === 'LONEWOLF';
    const norm = (raw) => Array.isArray(raw) ? raw
      : (raw?.data || raw?.documents || raw?.forms || raw?.content || []);
    try {
      const token = await getFirebaseToken();
      const authHeader = { 'Authorization': `Bearer ${token}` };
      if (isCO) {
        // SAME calls as the Docs tab's fetchDocuments CO branch: list every form in the
        // package, then resolve each form's REAL signed state from its live DocuSign
        // envelope recipients. The stored FormService status only ever advances to SENT
        // (nothing calls markSigned() once DocuSign reports completion), so filtering on
        // fi.status alone wrongly hid fully-signed forms from the picker. We bring all
        // forms first, compute the true signed state, THEN apply the signed-only filter.
        let packageId;
        try {
          const pkgResp = await apiClient.get(API_ENDPOINTS.formsPackagesByTransaction(id), { headers: authHeader });
          const pkgs = norm(pkgResp.data);
          packageId = pkgs?.[0]?.packageId || pkgs?.[0]?.id;
        } catch { /* no package generated yet */ }
        if (!packageId) return [];
        const formsResp = await apiClient.get(API_ENDPOINTS.formsPackageForms(packageId), { headers: authHeader });
        const rows = norm(formsResp.data).map(fi => {
          const st = (fi.status || '').toString().toLowerCase();
          const instanceId = fi.id || fi.instanceId;
          const versions = Array.isArray(fi.versions) ? fi.versions : [];
          // The envelope that carries live signing state: the instance's own
          // docusignEnvelopeId, or the latest version's envelope when sent more than once.
          const envelopeId = fi.docusignEnvelopeId || versions[versions.length - 1]?.envelopeId;
          return {
            documentId: `form-${instanceId}`,
            name: fi.formName || fi.formId || 'Colorado Form',
            source: 'form',
            formKind: 'formService',
            formRef: `${packageId}::${instanceId}`,
            _storedSigned: st === 'signed' || st === 'submitted',
            _envelopeId: envelopeId,
          };
        });
        // Live recipient status per envelope — mirrors the Docs tab: a form is Signed
        // once every recipient on its envelope has completed. Fail open per envelope
        // (keep the stored status) so one bad lookup never empties the whole list.
        const envelopeIds = [...new Set(rows.map(r => r._envelopeId).filter(Boolean))];
        const completedByEnv = new Map();
        await Promise.all(envelopeIds.map(async (envId) => {
          try {
            const recResp = await apiClient.get(API_ENDPOINTS.envelopeRecipients(envId), { headers: authHeader });
            const signers = recResp.data?.signers || [];
            completedByEnv.set(envId, signers.length > 0 && signers.every(s => (s.status || '').toLowerCase() === 'completed'));
          } catch (e) {
            console.warn('[AddDocuments] CO envelope recipients fetch failed for', envId, e?.message);
          }
        }));
        return rows.map(r => {
          const isSigned = r._storedSigned || (r._envelopeId ? completedByEnv.get(r._envelopeId) === true : false);
          return {
            documentId: r.documentId,
            name: r.name,
            userDisplayStatus: isSigned ? 'Signed' : undefined,
            source: r.source,
            isSigned,
            formKind: r.formKind,
            formRef: r.formRef,
          };
        }).filter(r => r.isSigned); // Forms section shows SIGNED forms only
      }
      if (isLW) {
        // A LoneWolf form carries NO signed status of its own — the
        // transaction-forms endpoint returns status/formtype/category as null.
        // The signed artifacts are the SIGNINGS: the new "Add documents" picker's
        // Forms section calls ONLY signing-documents and shows the finalized ones.
        // Each row downloads the signed PDF via lonewolfSignedDocument(signingId).
        const signingResp = await apiClient
          .get(API_ENDPOINTS.lonewolfSigningDocuments(id), { headers: authHeader })
          .catch(() => ({ data: [] }));
        return norm(signingResp.data)
          .filter(s => s?.finalized === true && s?.signingId)
          .map(s => ({
            documentId: `form-lw-${s.signingId}`,
            name: s.name || 'Lone Wolf Signing',
            userDisplayStatus: 'Signed',
            source: 'form',
            isSigned: true,
            formKind: 'lonewolfSigning',
            formRef: String(s.signingId),
          })); // Forms section shows SIGNED forms only
      }
      // California (DocuSign Rooms) forms
      const resp = await apiClient
        .get(API_ENDPOINTS.transactionDocusignDocuments(id), { headers: authHeader })
        .catch(() => ({ data: [] }));
      return norm(resp.data).filter(d => d?.documentId || d?.id).map(d => {
        const documentId = d.documentId || d.id;
        const isSigned = !!d.isSigned || (d.status || '').toLowerCase() === 'completed';
        return {
          documentId: `form-ds-${documentId}`,
          name: d.fileName || d.name || d.formName || 'Form',
          userDisplayStatus: d.status,
          source: 'form',
          isSigned,
          formKind: 'docusignDoc',
          formRef: String(documentId),
        };
      }).filter(r => r.isSigned); // Forms section shows SIGNED forms only
    } catch (e) {
      console.warn('[AddDocuments] fetchPickerForms failed:', e?.message);
      return [];
    }
  }, [id, transaction]);

  // When the "Add documents" picker opens, reset the new UI state and load the
  // Forms section. Covers every opener (listing terminal stage + buyer_rpa_signed).
  useEffect(() => {
    if (!listingPickerVisible) return;
    setPickerSearch('');
    setPickerTxnExpanded(true);
    setPickerFormsExpanded(true);
    setListingPickerFormsDocs([]);
    setPickerFormsLoading(true); // show a loader so the Forms section doesn't flash "No signed forms" while the async fetch is in flight
    let cancelled = false;
    fetchPickerForms()
      .then(rows => { if (!cancelled) setListingPickerFormsDocs(rows); })
      .catch(() => { if (!cancelled) setListingPickerFormsDocs([]); })
      .finally(() => { if (!cancelled) setPickerFormsLoading(false); });
    return () => { cancelled = true; };
  }, [listingPickerVisible, fetchPickerForms]);

  const submitListingDocPicker = useCallback(async (selectionsOverride) => {
    const selections = Array.isArray(selectionsOverride) ? selectionsOverride : Object.values(listingPickerSelections);
    if (selections.length === 0) { alert('Select at least one document.'); return; }
    const rpaCount = selections.filter(s => s.label === 'RPA').length;
    if (rpaCount !== 1) { alert('Tag exactly one document as RPA. Counter offers and other supporting docs ride along.'); return; }

    const priority = (label) => label === 'RPA' ? 0 : label === 'COUNTER_OFFER' ? 1 : 2;
    const sorted = [...selections].sort((a, b) => priority(a.label) - priority(b.label));

    setListingPickerVisible(false);
    setIsConverting(true);
    setConvertingStep('Generating documents...');
    try {
      const token = await getFirebaseToken();
      const authHeader = { 'Authorization': `Bearer ${token}` };
      const parts = [];

      // Download each selected doc and append to form — mirrors mobile submitListingDocPicker.
      // Source-aware: DocuSign-sourced docs (legacy seller_rpa_signed flow) download via
      // the envelope endpoint; GCS-sourced docs (Active Listing terminal stage) via the
      // transaction files endpoint — same distinction already used for buyer_rpa_signed above.
      for (const sel of sorted) {
        // OTHER + LISTING_AGREEMENT are stored but NOT sent to the parser — only the
        // Purchase Agreement + counter offers drive the milestone timeline.
        if (sel.label === 'OTHER' || sel.label === 'LISTING_AGREEMENT') continue;
        // Local-source rows (agent-added via Upload Document button) have
        // the Blob stashed on the selection — no download needed. Mirrors
        // mobile's handling of ListingDocSelection.source === 'local'.
        let rawBlob;
        if (sel.source === 'local' && sel.localBlob) {
          rawBlob = sel.localBlob;
          // Also upload the agent-added file to the transaction's GCS folder
          // so it lives on in the Docs tab after the parse flow completes.
          // FIRE-AND-FORGET: awaiting the upload before the multipart parse
          // POST serialises two large multipart calls on the same client (and
          // on mobile hit a ClientDisconnect / "timeout of 300000ms exceeded"
          // pattern on the parse call that followed). Parity with mobile.
          (async () => {
            try {
              const uploadFd = new FormData();
              uploadFd.append('file', rawBlob, sel.name);
              uploadFd.append('folder', 'purchase_agreement');
              await apiClient.post(
                API_ENDPOINTS.uploadTransactionFile(id),
                uploadFd,
                // Multipart upload: override apiClient's default application/json Content-Type,
                // else the gateway (consumes=multipart/form-data) rejects it with 415 -> 500.
                { headers: { ...authHeader, 'Content-Type': 'multipart/form-data' } }
              );
            } catch (uploadErr) {
              console.warn('[submitListingDocPicker] GCS upload of agent-added doc failed (parse flow unaffected)', uploadErr?.message);
            }
          })();
        } else {
          setConvertingStep('Downloading documents...');
          // Source-aware download endpoint: Lone Wolf finalised signings via
          // the lonewolfSignedDocument endpoint; GCS uploads via the files
          // endpoint; DocuSign envelopes via downloadDocument; Forms-section
          // docs via the endpoint matching their origin (FormService PDF /
          // Lone Wolf form / DocuSign document). Mirrors the picker's fetch.
          let dlEndpoint;
          if (sel.source === 'lonewolf') {
            dlEndpoint = API_ENDPOINTS.lonewolfSignedDocument(id, sel.documentId);
          } else if (sel.source === 'gcs') {
            dlEndpoint = API_ENDPOINTS.downloadTransactionFile(id, sel.documentId);
          } else if (sel.source === 'form') {
            if (sel.formKind === 'formService') {
              const [pkgId, instanceId] = String(sel.formRef || '').split('::');
              dlEndpoint = API_ENDPOINTS.formsDownloadPdf(pkgId, instanceId);
            } else if (sel.formKind === 'lonewolfSigning') {
              // LoneWolf Forms-section rows are finalized signings — download the
              // SIGNED PDF (formRef is the signingId), not the blank form.
              dlEndpoint = API_ENDPOINTS.lonewolfSignedDocument(id, sel.formRef);
            } else if (sel.formKind === 'lonewolfForm') {
              dlEndpoint = API_ENDPOINTS.lonewolfDownloadForm(id, sel.formRef);
            } else {
              dlEndpoint = API_ENDPOINTS.downloadDocument(id, sel.formRef);
            }
          } else {
            dlEndpoint = API_ENDPOINTS.downloadDocument(id, sel.documentId);
          }
          const dlResp = await apiClient.get(
            dlEndpoint,
            { headers: authHeader, responseType: 'blob' }
          );
          rawBlob = dlResp.data;
        }
        if (!rawBlob || rawBlob.size < 1000) throw new Error(`Downloaded file "${sel.name}" appears empty or invalid.`);
        const pdfBlob = rawBlob.type === 'application/pdf' ? rawBlob : new Blob([rawBlob], { type: 'application/pdf' });
        parts.push({ blob: pdfBlob, name: sel.name, label: sel.label }); // 'RPA' or 'COUNTER_OFFER'
      }

      setConvertingStep('Parsing documents...');
      // Layers network-level retry (10x, network-errors-only) and
      // application-level retry (4x total, empty timeline / Vertex quota).
      // Side comes from state (set by triggerActiveListingParseFlow) so the
      // same picker works for both listing-side terminal-stage AND buyer_rpa_signed.
      const parsed = await parseTransactionDocuments(pickerParseSide, parts, { firebaseToken: token });
      setParsedConversionResult(parsed);
      setShowConversionReviewModal(true);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Could not parse the offer documents.';
      alert(`Could not parse documents: ${message}`);
      setPendingConversionStageId(null);
    } finally {
      setIsConverting(false);
      setConvertingStep('');
    }
  }, [id, listingPickerSelections, pickerParseSide]);

  // Live ref so the "Process and generate timelines" Mark Complete can invoke the
  // picker's submit (parse + generate) WITHOUT rendering the modal — same flow, hidden.
  submitListingDocPickerRef.current = submitListingDocPicker;

  // "Add selected" (select-only picker): upload agent-added local files into the
  // purchase_agreement folder so they SHOW in the Upload Purchase Agreement stage, and
  // persist the tagged selection to metadata so the next stage ("Process and generate
  // timelines") parses exactly these — no re-selection, no parse here.
  const submitUploadOnlyPicker = useCallback(async () => {
    const selections = Object.values(listingPickerSelections);
    if (selections.length === 0) { setListingPickerVisible(false); return; }
    setListingPickerVisible(false);
    setIsLoadingListingPicker(true);
    try {
      const token = await getFirebaseToken();
      const authHeader = { 'Authorization': `Bearer ${token}` };
      // Upload each agent-added local file (tagged by label) to purchase_agreement/.
      for (const sel of selections) {
        if (sel.source === 'local' && sel.localBlob) {
          const fd = new FormData();
          fd.append('file', sel.localBlob, sel.name);
          fd.append('name', sel.name || 'Purchase Agreement');
          fd.append('folder', 'purchase_agreement');
          fd.append('type', sel.label === 'COUNTER_OFFER' ? 'COUNTER_OFFER'
            : sel.label === 'LISTING_AGREEMENT' ? 'LISTING_AGREEMENT'
            : 'PURCHASE_AGREEMENT');
          fd.append('category', sel.label === 'LISTING_AGREEMENT' ? 'LISTING' : 'PURCHASE');
          // Multipart upload: override apiClient's default application/json Content-Type,
          // else the gateway (consumes=multipart/form-data) rejects it with 415 -> 500.
          await apiClient.post(API_ENDPOINTS.uploadTransactionFile(id), fd, { headers: { ...authHeader, 'Content-Type': 'multipart/form-data' } });
        }
      }
      // Re-read the folder to resolve the real documentIds (local files just uploaded get
      // fresh ids), then persist the selection matched by file name + its RPA/counter tag.
      const resp = await apiClient.get(API_ENDPOINTS.transactionFiles(id), { headers: authHeader, params: { folder: 'purchase_agreement' } });
      const folder = resp.data?.data || resp.data?.content || resp.data || [];
      const byName = new Map((Array.isArray(folder) ? folder : []).map(d => [String(d.fileName ?? d.name ?? '').toLowerCase(), String(d.documentId ?? d.id)]));
      const persisted = selections
        .map(sel => {
          if (sel.source === 'local') {
            const gcsId = byName.get(String(sel.name).toLowerCase());
            return { documentId: String(gcsId ?? ''), name: sel.name, label: sel.label, source: 'gcs' };
          }
          // Preserve form / docusign / gcs selections intact — source + formKind/formRef
          // drive the correct download endpoint at parse time (a form downloaded via the
          // files endpoint 404s). Drop the local blob (not serialisable into metadata).
          const { localBlob, ...rest } = sel;
          return rest;
        })
        .filter(s => s.documentId && s.documentId !== 'undefined');
      const headers = { ...authHeader, 'Content-Type': 'application/json' };
      await apiClient.patch(
        API_ENDPOINTS.transactionById(id),
        { metadata: { ...transaction.metadata, purchaseAgreementSelection: persisted } },
        { headers }
      );
      const refreshed = await apiClient.get(API_ENDPOINTS.transactionById(id), { headers: authHeader });
      if (refreshed.data) setTransaction(refreshed.data);
      setPaRefreshToken(t => t + 1);
    } catch (e) {
      alert('Could not save documents: ' + (e?.response?.data?.message || e?.message || 'Please try again.'));
    } finally {
      setIsLoadingListingPicker(false);
    }
  }, [id, transaction, listingPickerSelections]);

  // "Add a Document" (Upload Purchase Agreement stage) opens the SAME picker in
  // select-only mode: the agent picks/uploads + tags the RPA/counters and the selection
  // is retained (NOT parsed). The next stage's Mark Complete parses it hidden.
  const openPurchaseAgreementSelectPicker = useCallback(
    () => triggerActiveListingParseFlow('upload_purchase_agreement', 'LISTING', true),
    [triggerActiveListingParseFlow],
  );

  // Handle delete calendar entry. Calendar-service events (Custom / Calendar-page)
  // delete from /calendar/events; service entries delete from /calendar-entries.
  //
  // For calendar-service events linked to a Service Request (create-service-request
  // flow): if that SR has already been sent to a provider, the backend cancels it
  // instead of hard-deleting (see CalendarEventService.deleteEvent) — preserves an
  // audit trail and notifies the SP. We confirm with the user before calling delete
  // in that case, using the same tiered SR correlation handleAssignServiceProvider
  // already uses (entry.serviceRequestId, falling back to entryType/serviceType —
  // the id back-link doesn't always persist server-side). The backend decides
  // delete-vs-cancel authoritatively regardless of what we resolve here; this lookup
  // only decides whether to show the confirmation prompt.
  // Actual delete/cancel API call — unconditional, called either directly (no
  // confirmation needed) or from the Yes button of the cancel-service confirm
  // modal below.
  const performDeleteCalendarEntry = useCallback(async (entryId, isCalendarEvent) => {
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      if (isCalendarEvent) {
        const response = await apiClient.delete(`${API_BASE_URL}/api/v1/calendar/events/${entryId}`, { headers });
        if (response?.data?.status === 'CANCELLED') {
          // Cancelled, not deleted — keep it visible in the timeline as an
          // audit trail instead of removing it.
          setCalendarEvents(prev => prev.map(e => (e.eventId === entryId ? { ...e, status: 'CANCELLED' } : e)));
        } else {
          setCalendarEvents(prev => prev.filter(e => e.eventId !== entryId));
        }
      } else {
        await apiClient.delete(API_ENDPOINTS.deleteCalendarEntry(id, entryId), { headers });
        setCalendarEntries(prev => prev.filter(e => e.id !== entryId));
      }
      console.log('Calendar entry deleted:', entryId);
      return true;
    } catch (error) {
      console.error('Error deleting calendar entry:', error);
      alert(error.response?.data?.message || 'Failed to delete calendar entry. Please try again.');
      return false;
    }
  }, [id]);

  const handleDeleteCalendarEntry = useCallback((entryId, isCalendarEvent = false) => {
    if (isCalendarEvent) {
      const event = calendarEvents.find(e => e.eventId === entryId);
      const eventServiceType = (event?.entryType || '').toLowerCase();
      const matchedSr =
        (event?.serviceRequestId && { id: event.serviceRequestId, status: srStatusById[event.serviceRequestId] }) ||
        (eventServiceType && srByServiceType[eventServiceType]) ||
        undefined;
      // Confirm only when the SR has actually been sent to a provider and
      // isn't already terminal — otherwise there's no provider surprise
      // to warn about, and per spec: "SRs not yet assigned/sent to any
      // provider can be deleted directly, without this confirmation
      // prompt." AWAITING_ASSIGNMENT counts as "sent" (with the Company);
      // PENDING/ACCEPTED/IN_PROGRESS are obviously with a provider.
      // Terminal states (CANCELLED / DECLINED / COMPLETED / EXPIRED) skip
      // the prompt because the provider is no longer actionable — a
      // cascade-delete on those is a no-op provider-side.
      //
      // `hasSrLink` covers the race where `event.serviceRequestId` is set
      // but `srStatusById[id]` hasn't populated yet (cache miss after
      // create, or between focus/refetch cycles). In that transient state
      // the row is definitely a live SR the requester needs to confirm on
      // — treating an undefined status as "not sent" silently deleted
      // Awaiting-Assignment SRs without warning (bug 2026-08-31 EOD).
      const linkedStatus = matchedSr?.status;
      const isTerminal =
        linkedStatus === 'CANCELLED'
        || linkedStatus === 'DECLINED'
        || linkedStatus === 'COMPLETED'
        || linkedStatus === 'EXPIRED';
      const hasSrLink = !!event?.serviceRequestId || !!matchedSr;
      const isSentToProvider = hasSrLink && !isTerminal;
      if (isSentToProvider) {
        // srId carried through so the confirm handler can fire the safety-net
        // cancel-SR call even when the server-side calendar-event → SR back-link
        // was never written (linkServiceRequest is best-effort at SR create).
        //
        // CRITICAL: only use event.serviceRequestId (the deterministic on-event
        // back-link), NEVER matchedSr.id when that came from the srByServiceType
        // fallback. When a transaction has two same-serviceType SRs (an old
        // CANCELLED one + a live one), the fallback resolves to whichever SR
        // sits first in sentSrs — which can be the *other* SR the user wasn't
        // touching. Cancelling that would silently kill an unrelated live SR.
        // matchedSr's serviceType fallback stays for the confirmation-modal
        // copy (wrong copy is recoverable; wrong cancel is not).
        setCancelServiceConfirm({ entryId, isCalendarEvent, srId: event?.serviceRequestId });
        return;
      }
      // No linked SR (never sent) OR SR is terminal → direct delete.
    }
    performDeleteCalendarEntry(entryId, isCalendarEvent);
  }, [calendarEvents, srStatusById, srByServiceType, performDeleteCalendarEntry]);

  const handleCancelServiceConfirmNo = useCallback(() => setCancelServiceConfirm(null), []);

  const handleCancelServiceConfirmYes = useCallback(async () => {
    if (!cancelServiceConfirm) return;
    const { entryId, isCalendarEvent, srId } = cancelServiceConfirm;
    setCancelServiceConfirm(null);
    const ok = await performDeleteCalendarEntry(entryId, isCalendarEvent);
    // Safety net: the calendar-service DELETE only cascades when the event's
    // server-side `serviceRequestId` back-link is populated. When that
    // best-effort link failed at SR-create time, the SR is left live in
    // user-service and the vendor keeps seeing Accept/Decline with no
    // SR_CANCELLED push/email. Explicitly cancel via the requester endpoint;
    // idempotent server-side (400 = cascade already handled it). Only run
    // when the calendar delete actually succeeded — otherwise we'd cancel
    // the SR while the timeline entry is still visible to the requester.
    if (ok && srId) {
      try {
        const firebaseToken = await getFirebaseToken();
        await apiClient.post(
          API_ENDPOINTS.cancelSentServiceRequest(srId),
          {},
          { headers: { Authorization: `Bearer ${firebaseToken}` } },
        );
      } catch (e) {
        const status = e?.response?.status;
        if (status !== 400 && status !== 404) {
          console.warn('SR cancel safety net failed:', srId, e?.message);
        }
      }
    }
  }, [cancelServiceConfirm, performDeleteCalendarEntry]);

  // Option B — mark a calendar-service event complete. Partial null-safe update:
  // send status + allDay (the only non-null-guarded field) so nothing else is clobbered.
  const handleCompleteCalendarEvent = useCallback(async (eventId) => {
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const existing = calendarEvents.find(e => e.eventId === eventId);
      await apiClient.put(
        `${API_BASE_URL}/api/v1/calendar/events/${eventId}`,
        { status: 'COMPLETED', allDay: !!existing?.allDay },
        { headers },
      );
      setCalendarEvents(prev => prev.map(e => (e.eventId === eventId ? { ...e, status: 'COMPLETED' } : e)));
    } catch (error) {
      console.error('Error completing calendar event:', error);
      alert(error.response?.data?.message || 'Failed to mark event complete. Please try again.');
    }
  }, [calendarEvents]);

  // Edit a calendar-service event's scheduled date (and optionally time).
  // Two writes on success:
  //   1. PUT /api/v1/calendar/events/{eventId} — moves the row on the
  //      Timeline. newDateStr = yyyy-MM-dd, newTimeStr = "HH:mm" or null.
  //   2. PATCH /api/v1/users/me/sent-service-requests/{srId} — syncs the
  //      SR's canonical requestedDate / requestedTime so the SP side sees
  //      the updated schedule (Job Detail reads from the SR, NOT from the
  //      calendar event, so without this step the SP shows stale values
  //      until the SR is otherwise touched). Only fires when the calendar
  //      event is linked to a PENDING SR — backend rejects requester edits
  //      once the SP accepts, and the SR-sync path is best-effort so a
  //      failure only logs and doesn't roll back the calendar update.
  //
  // ISO construction on the SR PATCH intentionally drops the trailing `Z`
  // so `new Date()` parses the picker values as LOCAL wall-clock time;
  // `.toISOString()` then correctly emits a UTC instant. Constructing with
  // `Z` (the previous behavior on other SR-create sites) mis-labeled the
  // local time as UTC — a 7:22 PM local pick was persisting as 19:22Z and
  // rendering on the SP side as 12:52 AM the next day (19:22 UTC +5:30).
  const handleEditCalendarEventDate = useCallback(async (eventId, newDateStr, newTimeStr) => {
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const existing = calendarEvents.find(e => e.eventId === eventId);

      // Save in UTC: convert the picker's local wall-clock values before
      // sending, and mark the event so any viewer renders it in their own
      // profile timezone instead of reading these digits as already-local.
      const { scheduledDate: utcDate, scheduledTime: utcTime } = toUtcWallClock(newDateStr, newTimeStr || null);
      const eventPayload = { scheduledDate: utcDate, allDay: !!existing?.allDay, timeZone: 'UTC' };
      if (newTimeStr) eventPayload.scheduledTime = utcTime;
      await apiClient.put(
        `${API_BASE_URL}/api/v1/calendar/events/${eventId}`,
        eventPayload,
        { headers },
      );
      setCalendarEvents(prev => prev.map(e => (
        e.eventId === eventId
          ? { ...e, scheduledDate: utcDate, timeZone: 'UTC', ...(newTimeStr ? { scheduledTime: utcTime } : {}) }
          : e
      )));

      // Tiered SR correlation. The .serviceRequestId back-link is unreliable
      // (systematically null on this dataset's events — CreateServiceRequestPage
      // back-link PUT silently fails in some flows), so we fall through to
      // matching by (serviceType + old scheduled date + old scheduled time)
      // against the sent-service-requests list. That handles the common case
      // where a transaction has multiple SRs of the same service type (each
      // one linked to a different calendar event) — srByServiceType picks
      // the first arbitrary one and would PATCH the wrong SR. Belt-and-
      // suspenders: last-resort fallback is still srByServiceType so a
      // service that only has one SR of its type still resolves cleanly.
      // Raw calendar event from state uses `eventType` (backend field name);
      // the timeline layer remaps it to `entryType` for its own render. This
      // handler reads directly off the raw object, so fall back through both
      // to survive either shape.
      const eventServiceType = (existing?.eventType || existing?.entryType || '').toLowerCase();
      const findSrByOldSchedule = () => {
        if (!eventServiceType || !existing?.scheduledDate) return null;
        // Match by service-type-lowercase AND by requestedTime whose date+HH:MM
        // matches the pre-edit calendar event schedule. Legacy SRs store the
        // wall-clock as UTC (buggy pre-fix write); post-fix SRs store as true
        // UTC. Both compare correctly if we render both back to the same
        // representation: extract YYYY-MM-DD and HH:MM from the SR's
        // requestedTime ISO in UTC (== the stored wall-clock digits either way
        // for pre-fix data, and matches the calendar event's raw fields for
        // post-fix data written by the same client).
        const matches = transactionServiceRequests.filter(sr => {
          if ((sr.serviceType || '').toLowerCase() !== eventServiceType) return false;
          const rt = sr.requestedTime || sr.requestedDate;
          if (!rt) return false;
          const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(rt);
          if (!m) return false;
          const [, dateStr, hh, mm] = m;
          const evtTime = existing.scheduledTime || '';
          const [evtHh = '', evtMm = ''] = evtTime.split(':');
          return dateStr === existing.scheduledDate && hh === evtHh && mm === evtMm;
        });
        // Exactly one match = confident correlation. More than one = ambiguous,
        // fall through to the arbitrary first-match to preserve prior behaviour.
        return matches.length === 1
          ? { id: matches[0].id, status: matches[0].status }
          : null;
      };
      const matchedSr =
        (existing?.serviceRequestId && { id: existing.serviceRequestId, status: srStatusById[existing.serviceRequestId] }) ||
        findSrByOldSchedule() ||
        (eventServiceType && srByServiceType[eventServiceType]) ||
        null;
      // PENDING = individual freelancer or already-assigned staff inspector
      // hasn't accepted yet. AWAITING_ASSIGNMENT = routed to a Company vendor
      // whose admin hasn't picked a staff inspector yet. Both are
      // pre-acceptance and editable by the requester per user-service's
      // updateAsRequester rule-set (2026-08-26).
      if (matchedSr?.id
          && (matchedSr?.status === 'PENDING' || matchedSr?.status === 'AWAITING_ASSIGNMENT')) {
        const requestedDateISO = toUtcInstant(newDateStr, null);
        try {
          await apiClient.patch(
            API_ENDPOINTS.patchSentServiceRequest(matchedSr.id),
            {
              requestedDate: requestedDateISO,
              // requestedTime is no longer written from the client (Due-By-
              // Time input was removed; backend accepts null).
            },
            { headers },
          );
        } catch (patchErr) {
          // Best-effort — the calendar event already updated, so we don't
          // roll back or block the user. Ops can retry via the endpoint if
          // needed. Common non-fatal case: SR just flipped to ACCEPTED
          // between the calendar PUT and this PATCH (400 "already past
          // PENDING / AWAITING_ASSIGNMENT").
          console.warn('[EditEventDate] Calendar event updated but SR sync failed:', patchErr?.response?.status, patchErr?.response?.data?.message);
        }
      }
    } catch (error) {
      console.error('Error updating calendar event date:', error);
      alert(error.response?.data?.message || 'Failed to update event date. Please try again.');
    }
  }, [calendarEvents, srStatusById, srByServiceType, transactionServiceRequests]);

  // Assign/Reassign a service provider to an existing service-typed calendar row
  // (mirrors mobile handleAssignSpToEvent). Errors are left to throw — the
  // AssignServiceProviderModal shows them inline instead of via alert().
  const handleAssignServiceProvider = useCallback(async (entry, provider, scheduledDate, scheduledTime) => {
    const firebaseToken = await getFirebaseToken();
    const headers = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
    const providerId = provider.id || provider.providerId || provider.serviceProviderId || provider.userId || provider.firebaseUid || null;
    const providerName = provider.fullName || provider.name || [provider.firstName, provider.lastName].filter(Boolean).join(' ') || '';
    const providerEmail = provider.email || '';

    // Same tiered correlation the "Assign/Reassign SP" button label already
    // uses for display (TransactionTimeline.js) — tier-1 (entry.serviceRequestId)
    // silently fails in practice because the id back-link PUT doesn't always
    // persist server-side (see srByServiceType's comment above). Using ONLY
    // tier-1 here (as this handler previously did) meant clicking "Reassign SP"
    // on a row that already had a PENDING request almost always fell through
    // to the create-new branch below, silently creating a DUPLICATE service
    // request instead of reassigning the existing one — the tier-3 serviceType
    // fallback is the one that actually resolves in production. Tier-2
    // (provider email) is skipped here because /sent-service-requests always
    // returns providerEmail: null, so srStatusByProviderEmail can never supply
    // the SR id this handler needs to PATCH against.
    const eventServiceType = (entry.entryType || '').toLowerCase();
    // Explicit target first: the View Details modal passes the SR the
    // requester is looking at (targetServiceRequestId). Then the row's own
    // back-link. Only then the serviceType guess.
    const explicitSr = entry.targetServiceRequestId
      ? {
          id: entry.targetServiceRequestId,
          status: srStatusById[entry.targetServiceRequestId] || entry.targetServiceRequestStatus,
        }
      : null;
    const ownLinkedSr = entry.serviceRequestId
      ? { id: entry.serviceRequestId, status: srStatusById[entry.serviceRequestId] }
      : null;
    const matchedSr =
      explicitSr ||
      ownLinkedSr ||
      (eventServiceType && srByServiceType[eventServiceType]) ||
      undefined;
    // True only when matchedSr came from the serviceType guess — i.e. it may
    // be ANOTHER row's SR (newest of that type), not this row's own.
    const isServiceTypeGuess = !explicitSr && !ownLinkedSr && !!matchedSr;
    const linkedStatus = matchedSr?.status;
    const resolvedSrId = matchedSr?.id;

    // Treat the picker values as wall-clock time in the viewer's own profile
    // timezone (not the device's timezone) and convert to a real UTC instant.
    // requestedTime is no longer written from the client — the Due-By-Time
    // input was removed and the backend accepts null.
    const requestedDateISO = toUtcInstant(scheduledDate, null);

    // Same UTC conversion for the calendar-event side of this write — the SR
    // side above already saves correctly (full ISO instant); the calendar
    // event's own scheduledDate/scheduledTime were still being sent as raw,
    // unconverted local digits until now.
    // Date-only events (no stored scheduledTime — every service row created
    // from this page) stay date-only: converting a time here would shift the
    // stored date, and calendar-service wouldn't persist the time anyway.
    // Events that do carry a stored time keep the original conversion.
    const { scheduledDate: utcScheduledDate, scheduledTime: utcScheduledTime } =
      toUtcWallClock(scheduledDate, entry.scheduledTime ? (scheduledTime || null) : null);

    let srId = resolvedSrId;
    let needsBackLink = false;
    // Full SR object returned by the POST path — used below to optimistic-
    // insert into transactionServiceRequests so the timeline pill renders
    // immediately without waiting for the next /sent-service-requests
    // refetch. `null` when we took the PATCH-in-place branch (existing SR
    // already lives in the map).
    let createdSrResponse = null;

    // PATCH-in-place for any pre-acceptance state — backend
    // `updateAsRequester` allows edits while status is PENDING (individual
    // SP or already-routed inspector awaiting Accept) or AWAITING_ASSIGNMENT
    // (Company SP hasn't picked an inspector yet). Without AWAITING_ASSIGNMENT
    // in this gate, reassigning a Company-routed SR fell through to the
    // create-new branch below and duplicated the SR — burned 2026-08-31.
    //
    // Phase 2 (2026-09-07): DECLINED is also PATCH-able when the
    // requester is reassigning — backend guard was extended to accept
    // DECLINED SRs with a providerId change. Reusing the same SR
    // preserves the audit trail (previousProviderId, status flip DECLINED
    // → PENDING/AWAITING_ASSIGNMENT) and avoids the timeline-clutter of
    // an abandoned DECLINED sibling next to a fresh POST. CANCELLED /
    // EXPIRED remain terminal (backend still rejects).
    //
    // A DECLINED SR is only reused when it's this row's own SR (explicit
    // target or back-link). A DECLINED SR reached via the serviceType guess
    // is most likely an older sibling of the same type — PATCHing it would
    // hijack/no-op that old SR instead of creating this row's new request.
    const isReassignable =
      linkedStatus === 'PENDING'
      || linkedStatus === 'AWAITING_ASSIGNMENT'
      || (linkedStatus === 'DECLINED' && !isServiceTypeGuess);
    if (resolvedSrId && isReassignable) {
      // Reassign the existing pending request in place instead of creating a new one.
      //
      // Compare the picker output against the stored value in the SAME encoding.
      // AssignServiceProviderModal now pre-fills its pickers in viewer-zone
      // wall-clock (so the requester sees the same digits the timeline row
      // above shows), but entry.scheduledDate/scheduledTime are stored as
      // UTC wall-clock when the event carries `timeZone: 'UTC'`. Comparing
      // scheduledDate (viewer-zone) directly against entry.scheduledDate
      // (UTC) would ALWAYS mismatch for any UTC-encoded event and force the
      // requestedDate/requestedTime keys into every PATCH — that fires the
      // backend's scheduleChanged path, which sends an SR_EDITED "schedule
      // moved" push to the provider even when the requester only changed
      // provider. Round-trip the picker output through toUtcWallClock (done
      // above as utcScheduledDate / utcScheduledTime) and compare against
      // the stored UTC values.
      const isUtcEncoded = entry.timeZone === 'UTC';
      const compareLeftDate = isUtcEncoded ? utcScheduledDate : scheduledDate;
      const compareLeftTime = isUtcEncoded ? (utcScheduledTime || '') : scheduledTime;
      const dateChanged = compareLeftDate !== entry.scheduledDate;
      const timeChanged = compareLeftTime !== (entry.scheduledTime || '');
      await apiClient.patch(API_ENDPOINTS.patchSentServiceRequest(resolvedSrId), {
        providerId,
        ...(dateChanged && { requestedDate: requestedDateISO }),
        // requestedTime is no longer written from the client (Due-By-Time
        // input was removed; backend accepts null).
      }, { headers });
      // Back-link even on reassign when it was missing before (resolvedSrId came
      // from the serviceType fallback, not entry.serviceRequestId) — this is
      // what actually breaks the tier-1-always-fails cycle: without writing it
      // here, every future click on this row keeps needing the same fallback.
      if (resolvedSrId !== entry.serviceRequestId) {
        srId = resolvedSrId;
        needsBackLink = true;
      }
    } else {
      // No link yet, or the prior link was DECLINED — create a fresh service request.
      const serviceTitle = SERVICE_ENTRY_LABELS[entry.entryType] || entry.title || entry.entryType;
      // Resolve address at call time (NOT via the stale-closure `getFullAddress`)
      // and cascade through the field-name variants the transaction / linked
      // properties actually use. Bug 2026-08-31: earlier version called
      // getFullAddress() which read from a first-render `transaction` snapshot
      // — before the transaction had loaded — so every reassign shipped
      // propertyAddress: "" even when the sidebar clearly showed an address.
      const t = transaction || {};
      const rootAddress = [t.propertyAddress, t.propertyCity, t.propertyState, t.propertyZip]
        .filter(Boolean)
        .join(', ');
      // Fall back to the entry's own location (calendar events carry it) if
      // the transaction root has nothing populated (typical for prospects
      // whose address only lives on the interested-property record).
      const resolvedAddress = rootAddress || entry.location || '';
      // Pass calendarEventId so user-service atomically back-links the SR
      // onto the calendar event (2026-09-11 fix — replaces the client-side
      // PUT below which raced/failed silently on multi-SR-same-day flows).
      const res = await apiClient.post(API_ENDPOINTS.createServiceRequest, {
        providerId,
        serviceType:     serviceTitle,
        propertyAddress: resolvedAddress,
        requestedDate:   requestedDateISO,
        urgencyLevel:    'NORMAL',
        description:     `${serviceTitle} service request`,
        transactionId:   id,
        calendarEventId: entry.id,
      }, { headers });
      // Backend returns the full ServiceRequestResponse. Prefer that over
      // reconstructing — see the optimistic insert below.
      createdSrResponse = res.data?.data || res.data || null;
      srId = createdSrResponse?.id || createdSrResponse?.requestId;
      needsBackLink = true;
    }

    // Mirror schedule/invitee onto the calendar event. The SR back-link is
    // now written server-side atomically (see createServiceRequest's
    // calendarEventId param above), so no serviceRequestId PUT here.
    const existing = calendarEvents.find(e => e.eventId === entry.id);
    const invitees = [{ ...(providerName && { name: providerName }), ...(providerEmail && { email: providerEmail }) }];
    await apiClient.put(
      `${API_BASE_URL}/api/v1/calendar/events/${entry.id}`,
      {
        scheduledDate: utcScheduledDate,
        scheduledTime: utcScheduledTime,
        timeZone: 'UTC',
        invitees,
        allDay: !!existing?.allDay,
      },
      { headers },
    );

    setCalendarEvents(prev => prev.map(e => (e.eventId === entry.id ? {
      ...e,
      scheduledDate: utcScheduledDate, scheduledTime: utcScheduledTime, timeZone: 'UTC', invitees,
      ...(needsBackLink && srId && { serviceRequestId: srId }),
    } : e)));
    if (needsBackLink && srId) {
      // Optimistic insert so the timeline pill renders immediately
      // (before the async loadSentSrs refetch resolves). Prefer the full
      // ServiceRequestResponse returned by the POST — it has the
      // authoritative status the backend picked (AWAITING_ASSIGNMENT for
      // Company vendors, PENDING for individuals), plus serviceType,
      // requestedDate/Time, routedByCompanyId that the correlation and
      // Reassign-gate code all read. Fall back to a status stub only
      // when we don't have the full response (e.g. PATCH-in-place
      // branch — the SR already exists in the map, so this insert is a
      // no-op after the id-dedup below).
      setTransactionServiceRequests(prev => {
        const already = prev.some(sr =>
          (sr.id || sr.requestId || sr.serviceRequestId) === srId);
        if (already) return prev;
        const optimistic = createdSrResponse || {
          id: srId,
          status: 'PENDING',
          providerId,
          providerEmail,
          serviceType: SERVICE_ENTRY_LABELS[entry.entryType] || entry.entryType,
          requestedDate: requestedDateISO,
          requestedTime: null,
          transactionId: id,
        };
        return [optimistic, ...prev];
      });
    }
  }, [calendarEvents, id, srStatusById, srByServiceType, transaction]);

  // Provider list fetch for AssignServiceProviderModal's picker. Pulls the
  // full SP list for the service type — the modal shows everything grouped
  // by Company / Individual instead of typeahead-narrowed suggestions.
  //
  // Historical notes: this used to filter client-side against a name term
  // and slice to 8 results (typeahead pattern). Both caps were leftover
  // after the 2026-08-31 picker refactor and were silently dropping SPs
  // beyond the first 8; also the backend page size defaulted to 20 without
  // an explicit `size` param. Both fixed here — explicit `size: 100` + no
  // client cap. `term` is ignored (kept in the signature for callsite
  // compatibility with the old shape).
  const handleSearchServiceProviders = useCallback(async (entryType, _term, options = {}) => {
    // entryType is an internal picker key (e.g. 'inspection'); the static map
    // resolves it to the canonical label ('Home Inspection'). Prefer the
    // catalog UUID for the wire call — rename-safe, matches mobile's picker,
    // matches backend's PREFERRED contract. Falls through to the label when
    // the catalog fetch hadn't landed yet (backend still accepts labels).
    const apiLabel = SERVICE_TYPE_TO_API_PARAM[entryType] || entryType;
    const apiParam = vendorServiceTypeOptions.find(o => o.name === apiLabel)?.id || apiLabel;
    const token = await getFirebaseToken();
    // Phase-4 SP-radius switch. Prefer the transaction's geocoded property
    // coords (Phase-2 on transaction-service — TransactionResponse.property
    // Latitude/Longitude). Falls back to the requester's own coords when the
    // transaction has no property (prospect flow), then finally to the
    // pre-radius state predicate so a caller with neither coords nor state
    // still gets *some* filter. When the backend receives coords, it drops
    // the state predicate and applies Haversine within 25 mi (default).
    //
    // `options.expandedSearch=true` (flipped by the modal's "Show all
    // providers" toggle) drops the coord fallback entirely — the requester
    // asked to see out-of-radius SPs, so we fall straight to the state
    // predicate.
    //
    // `options.name` (>= 3 chars, from the picker's search input) is passed
    // through as the backend `name` filter (matches firstName/lastName/
    // businessName/email in UserController.listServiceProviderUsers).
    // Caller is expected to also set expandedSearch=true when name is set —
    // name-based lookup only makes sense state-wide.
    const { expandedSearch = false, name } = options;
    const propertyLat = finiteOrNull(transaction?.propertyLatitude);
    const propertyLng = finiteOrNull(transaction?.propertyLongitude);
    let requesterLat = null;
    let requesterLng = null;
    let stateParam;
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      requesterLat = finiteOrNull(u.latitude);
      requesterLng = finiteOrNull(u.longitude);
      if (transaction?.propertyState) {
        stateParam = transaction.propertyState;
      } else {
        stateParam = u.licenseState || u.stateCode || u.state || undefined;
      }
    } catch {
      // localStorage parse failure — leave everything undefined; the
      // backend defaults to no-filter and every SP surfaces.
    }
    const useCoords = !expandedSearch && propertyLat != null && propertyLng != null;
    const useRequesterCoords = !expandedSearch && !useCoords && requesterLat != null && requesterLng != null;
    const resp = await apiClient.get(API_ENDPOINTS.serviceProviders, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        serviceType: apiParam,
        size: 100,
        sort: 'rating,desc',
        ...(useCoords ? { referenceLat: propertyLat, referenceLng: propertyLng } : {}),
        ...(useRequesterCoords ? { referenceLat: requesterLat, referenceLng: requesterLng } : {}),
        ...(!useCoords && !useRequesterCoords && stateParam ? { state: stateParam } : {}),
        ...(name ? { name } : {}),
      },
    });
    return Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
  }, [transaction?.propertyState, transaction?.propertyLatitude, transaction?.propertyLongitude, vendorServiceTypeOptions]);

  // §2.3 — edit a prospect stage's suggested date. Upserts suggestedDate on the stage
  // (preserving status/completedAt) and persists via PATCH; the returned transaction
  // re-renders and the timeline re-sorts by date. newDateStr = yyyy-MM-dd.
  const handleEditStageDate = useCallback(async (stageId, newDateStr) => {
    try {
      const firebaseToken = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const now = new Date().toISOString();
      const txnState = transaction?.agentLicenseState || 'CA';
      let currentTimeline = transaction?.metadata?.prospectTimeline;
      if (!currentTimeline?.stages?.length) {
        await ensureStagesLoaded(txnState);
        const txnSideUpper = (transaction?.side || transaction?.type || '').toUpperCase();
        const isB = txnSideUpper === 'BUYING' || txnSideUpper === 'PURCHASE';
        const defs = getCachedStages(isB ? 'BUYING' : 'LISTING', txnState);
        const initAt = transaction?.createdAt || now;
        currentTimeline = {
          side: isB ? 'BUYING' : 'LISTING',
          startedAt: initAt,
          stages: defs.map((def, i) => ({
            stageId: def.id,
            status: i === 0 ? 'COMPLETED' : 'NOT_STARTED',
            ...(i === 0 ? { completedAt: initAt } : {}),
          })),
        };
      }
      const existingIndex = currentTimeline.stages.findIndex(s => s.stageId === stageId);
      const updatedStages = existingIndex >= 0
        ? currentTimeline.stages.map(s => (s.stageId === stageId ? { ...s, suggestedDate: newDateStr } : s))
        : [...currentTimeline.stages, { stageId, status: 'NOT_STARTED', suggestedDate: newDateStr }];
      const updatedTimeline = { ...currentTimeline, stages: updatedStages };
      const response = await apiClient.patch(
        API_ENDPOINTS.transactionById(id),
        { metadata: { prospectTimeline: updatedTimeline } },
        { headers },
      );
      if (response.data) setTransaction(response.data);
    } catch (error) {
      console.error('Error updating stage date:', error);
      alert(error.response?.data?.message || 'Failed to update date. Please try again.');
    }
  }, [transaction, id]);

  // Function to create prospect timeline stages for BUYING or LISTING transactions
  // Uses getCachedStages() from prospectStageCache — same as mobile's createInitialProspectTimeline()
  const createProspectTimelineStages = useCallback(async (transactionSide) => {
    try {
      const firebaseToken = await getFirebaseToken();

      const isBuying = transactionSide === 'BUYING' || transactionSide === 'PURCHASE';
      const side = isBuying ? 'BUYING' : 'SELLING';

      // Ensure stage definitions are loaded from API before building initial timeline
      const txnState = transaction?.agentLicenseState || 'CA';
      await ensureStagesLoaded(txnState);
      const defs = getCachedStages(isBuying ? 'BUYING' : 'LISTING', txnState);
      const now = transaction?.createdAt || new Date().toISOString();
      const stages = defs.map((def, index) => ({
        stageId: def.id,
        status: index === 0 ? 'COMPLETED' : 'NOT_STARTED',
        ...(index === 0 ? { completedAt: now } : {}),
      }));

      const prospectTimelinePayload = {
        metadata: {
          prospectTimeline: {
            side: side,
            stages: stages
          }
        }
      };

      console.log('Creating prospect timeline stages:', prospectTimelinePayload);

      const response = await apiClient.patch(
        API_ENDPOINTS.transactionById(id),
        prospectTimelinePayload,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Prospect timeline stages created successfully:', response.data);

      // Update the transaction state with the new data
      if (response.data) {
        setTransaction(response.data);
      }

      return response.data;
    } catch (error) {
      console.error('Error creating prospect timeline stages:', error);
      throw new Error(error.response?.data?.message || 'Failed to create prospect timeline stages');
    }
  }, [id, transaction?.createdAt]);

  // Auto-apply template — mirrors mobile TimelineTab.tsx exactly.
  // Mobile rule: if milestones already exist, trust them and do nothing regardless of
  // their metadata shape or which user role is viewing the transaction.
  // Only apply a standard template when the milestone list is completely empty.
  useEffect(() => {
    const autoApplyTemplate = async () => {
      if (
        !transaction ||
        transaction?.status?.toUpperCase() === 'PENDING' ||
        isLoading ||
        isMilestonesLoading ||
        isApplyingTemplate ||
        templateApplied
      ) return;

      // Case 0: LISTING-side transactions never auto-apply a template — mirrors
      // mobile TimelineTab.tsx's explicit guard. The listing flow's milestones
      // come entirely from the backend (either the create-payload response or
      // the Active Listing checklist's Convert-to-Active endpoint). Without
      // this, an Active LISTING transaction sitting mid-checklist (activeListingTimeline
      // present, 0 milestones yet) would have this effect wrongly seed a
      // STANDARD_SALE template before the agent finishes "Process and generate
      // timelines".
      const txnSideForTemplate = (transaction?.side || transaction?.type || '').toUpperCase();
      if (txnSideForTemplate === 'LISTING' || txnSideForTemplate === 'SALE') {
        setTemplateApplied(true);
        return;
      }

      // Case 1: Milestones exist — trust them as-is and stop.
      // This is the ONLY check mobile performs: milestones.length > 0 → mark done → return.
      // Never delete or re-apply for any user role (agent, buyer, seller, TC).
      if (milestones.length > 0) {
        setTemplateApplied(true);
        return;
      }

      // Case 2a: Prospect-flow transactions own their timeline via the parse+
      // convertToActive pipeline. If metadata.prospectTimeline is present the
      // transaction was created as prospect, and the only legitimate way for
      // its milestones to land is persistReviewedTimeline. A zero-milestone
      // state here means the parse flow failed mid-way (e.g. acceptanceDate
      // couldn't be resolved) — DO NOT paper over it with the default
      // template, because that silently replaces whatever the agent parsed
      // from the signed RPA with Oct-7-anchored CA.STANDARD_PURCHASE rows.
      // Let the agent see the empty timeline and re-trigger Mark As.
      if (transaction?.metadata?.prospectTimeline) {
        setTemplateApplied(true);
        return;
      }

      // Case 2b: No milestones at all.
      // Guard: if this transaction was created via the Buying+Active wizard the AI-parsed
      // milestones may still be propagating from the backend — don't overwrite them.
      if (!!localStorage.getItem(`txn_ai_timeline_${id}`)) {
        setTemplateApplied(true);
        return;
      }

      // Case 3: No milestones and no AI-timeline flag → apply standard template.
      // Matches mobile: applyMilestoneTemplate → fetchTransaction(false).
      const templateName = getTemplateName(transaction);
      try {
        setIsApplyingTemplate(true);
        setIsMilestonesLoading(true);
        console.log('Auto-applying template (no milestones):', templateName);
        await applyMilestoneTemplate(templateName);
        await fetchMilestones();
        setTemplateApplied(true);
      } catch (err) {
        console.error('Failed to auto-apply template:', err);
        setTemplateApplied(true); // prevent infinite retry
      } finally {
        setIsApplyingTemplate(false);
        setIsMilestonesLoading(false);
      }
    };

    autoApplyTemplate();
  }, [transaction?.id, transaction?.status, milestones.length, isLoading, isMilestonesLoading, isApplyingTemplate, templateApplied, applyMilestoneTemplate, fetchMilestones]);

  // Auto-create prospect timeline stages for BUYING or LISTING transactions when Agent is logged in
  // Only runs when transaction status is PENDING (Prospect phase)
  useEffect(() => {
    const autoCreateProspectStages = async () => {
      // Get current user info
      const user = getUserInfo();
      if (!user) return;

      // Check if active role is an agent role (sellers/buyers can't PATCH transaction metadata)
      const activeRole = getActiveRoleFromStorage()?.toUpperCase() || '';
      if (!['AGENT', 'BUYER_AGENT', 'LISTING_AGENT', 'TRANSACTION_COORDINATOR'].includes(activeRole)) return;

      // Get transaction side/type
      const transactionSide = transaction?.side?.toUpperCase() || transaction?.type?.toUpperCase() || '';

      // Check if it's a BUYING/PURCHASE or LISTING/SALE transaction
      const isBuyingTransaction = transactionSide === 'BUYING' || transactionSide === 'PURCHASE';
      const isListingTransaction = transactionSide === 'LISTING' || transactionSide === 'SALE';
      const isValidTransaction = isBuyingTransaction || isListingTransaction;

      // Only proceed if transaction status is PENDING (Prospect phase)
      const transactionStatus = transaction?.status?.toUpperCase() || '';
      const isProspectPhase = transactionStatus === 'PENDING';

      // Check if prospect timeline already exists in metadata
      const hasProspectTimeline = transaction?.metadata?.prospectTimeline?.stages?.length > 0;

      if (
        transaction &&
        isValidTransaction &&
        isProspectPhase &&
        !hasProspectTimeline &&
        !isLoading &&
        !isCreatingProspectStages &&
        !prospectStagesCreated
      ) {
        try {
          setIsCreatingProspectStages(true);
          const transactionTypeLabel = isBuyingTransaction ? 'BUYING' : 'LISTING';
          console.log(`Auto-creating prospect timeline stages for ${transactionTypeLabel} transaction in Prospect phase`);

          await createProspectTimelineStages(transactionSide);

          setProspectStagesCreated(true);
        } catch (err) {
          console.error('Failed to auto-create prospect timeline stages:', err);
          // Mark as created even on error to prevent infinite retry
          setProspectStagesCreated(true);
        } finally {
          setIsCreatingProspectStages(false);
        }
      }
    };

    autoCreateProspectStages();
  }, [transaction?.id, transaction?.side, transaction?.type, transaction?.status, transaction?.metadata?.prospectTimeline, isLoading, isCreatingProspectStages, prospectStagesCreated, createProspectTimelineStages]);

  // Show "Review your transaction timelines" popup only when the transaction
  // transitions from PENDING → ACTIVE during the current page session.
  // Navigating to a transaction that is already Active must NOT trigger this modal.
  // prevTransactionStatusRef tracks the status seen on the previous render so we
  // can detect the live transition rather than checking static post-prospect status.
  useEffect(() => {
    if (!transaction || isLoading) return;
    const currentStatus = transaction.status;
    const prevStatus = prevTransactionStatusRef.current;

    // Only show when the status just changed from PENDING to ACTIVE in this session.
    // On initial page load prevStatus is null (not 'PENDING'), so an already-Active
    // transaction will never satisfy this condition.
    if (prevStatus === 'PENDING' && currentStatus === 'ACTIVE') {
      const key = `txn_timeline_intro_${id}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, '1');
        setShowTimelineIntroModal(true);
      }
    }

    prevTransactionStatusRef.current = currentStatus;
  }, [transaction?.status, id, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build full address from transaction data
  const getFullAddress = () => {
    if (!transaction) return '';
    const parts = [
      transaction.propertyAddress,
      transaction.propertyCity,
      transaction.propertyState,
      transaction.propertyZip
    ].filter(Boolean);
    return parts.join(', ') || 'No address';
  };

  // Get transaction type display
  const getTransactionType = () => {
    if (!transaction) return '';
    return transaction.side;
  };

  const handleBackToTransactions = () => {
    // Honour an explicit returnTo passed via location.state (e.g. the
    // Payments page "View" button sets state.returnTo = '/profile' so the
    // user lands back on the Payments screen rather than the generic
    // Transactions list). Defaults to /transactions.
    const returnTo = location.state?.returnTo;
    navigate(returnTo || '/transactions');
  };

  // Cancel transaction handler
  const handleCancelTransaction = async () => {
    setCancelError('');
    if (!cancelReason.trim()) {
      setCancelError('Please provide a reason for cancellation.');
      return;
    }
    setIsCancelling(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.changeTransactionStatus(id),
        { status: 'CANCELLED', reason: cancelReason.trim() },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setShowCancelModal(false);
      setCancelReason('');
      navigate('/transactions');
    } catch (error) {
      console.error('Error cancelling transaction:', error);
      setCancelError(error.response?.data?.message || 'Failed to cancel transaction. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Reset calendar form to defaults
  const resetCalendarForm = () => {
    // "Now" as wall-clock date in the viewer's own profile timezone — not the
    // device clock/timezone, which can silently disagree (wrong laptop clock,
    // traveling user, etc.). Due By Time was removed from the UI; pin to
    // 23:59 (end of the selected date) so the deadline is unambiguous.
    const { localDate: currentDate } = nowWallClockInViewerZone();
    setCalendarForm({
      title: '',
      entryType: '',
      category: '',
      providerType: 'EXTERNAL_VENDOR',
      providerName: '',
      providerPhone: '',
      providerEmail: '',
      serviceType: '',
      customServiceType: '',
      scheduledDate: currentDate,
      scheduledTime: '23:59',
      duration: 60,
      location: getFullAddress() !== 'No address' ? getFullAddress() : '',
      notes: '',
      estimatedAmount: '',
      urgencyLevel: 'NORMAL',
      externalAcknowledged: false
    });
    setCalendarEntryError('');
    setProviderSource('network');
    setSelectedProviderId(null);
    setShowProviderPickerModal(false);
    setProviderList([]);
    setProviderListLoading(false);
  };

  // Update a single calendar form field
  const updateCalendarField = (field, value) => {
    setCalendarForm(prev => ({ ...prev, [field]: value }));
  };

  // Map form values to API entryType (match mobile app - readable strings)
  const ENTRY_TYPE_MAP = {
    consultation: 'Lead Consultation',
    showing: 'Property Showing',
    meeting: 'Client Meeting',
    'open-house': 'Open House',
    walkthrough: 'Final Walkthrough',
    other: 'Other'
  };

  const SERVICE_TYPE_MAP = {
    inspection: 'Home Inspection',
    appraisal: 'Appraisal',
    repairs: 'Repairs',
    staging: 'Staging',
    photography: 'Photography',
    other: 'Other'
  };

  // Service type options per provider type (mirrors mobile TRANSACTION_PARTY_SERVICE_TYPES / SERVICE_REQUEST_TYPES)
  const TRANSACTION_PARTY_SERVICE_OPTIONS = [
    { value: 'escrow_company',  label: 'Escrow Company' },
    { value: 'title_company',   label: 'Title Company' },
    { value: 'mortgage_lender', label: 'Mortgage Lender' },
  ];

  // Map duration number to string (match mobile app format)
  const DURATION_MAP = {
    15: '15 minutes',
    30: '30 minutes',
    60: '1 hour',
    90: '1.5 hours',
    120: '2 hours',
    180: '3 hours',
    240: '4 hours',
    480: 'All day'
  };

  // Company-vendor SPs display businessName; individuals fall through to
  // fullName / firstName+lastName / email. Kept as a single resolver so the
  // picker list, the selected-provider chip, and the persisted providerName
  // all agree.
  const displayNameForProvider = (p) => {
    const isCompany = (p.vendorType || '').toUpperCase() === 'COMPANY';
    if (isCompany && p.businessName) return p.businessName;
    return p.fullName
      || p.name
      || [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
      || p.email
      || '';
  };

  // Load the full SP list for the current Service Type when the picker
  // opens. Sorted by rating desc server-side. One fetch per open — closing
  // and reopening (or switching Service Type) re-fetches.
  // Reset the expand-search toggle whenever the picker closes so the next
  // open starts on the nearby list even if the last one was expanded.
  useEffect(() => {
    if (!showProviderPickerModal) {
      setCalendarExpandedSearch(false);
      setProviderSearchQuery('');
      setDebouncedProviderSearch('');
    }
  }, [showProviderPickerModal]);

  useEffect(() => {
    if (!showProviderPickerModal) return undefined;
    if (!calendarForm.serviceType) {
      setProviderList([]);
      return undefined;
    }
    // Same id-preferred resolution as handleSearchServiceProviders above.
    const apiLabel = SERVICE_TYPE_TO_API_PARAM[calendarForm.serviceType] || calendarForm.serviceType;
    const apiParam = vendorServiceTypeOptions.find(o => o.name === apiLabel)?.id || apiLabel;
    let cancelled = false;
    setProviderListLoading(true);
    (async () => {
      try {
        const token = await getFirebaseToken();
        // Phase-4 SP-radius switch — same shape as handleSearchServiceProviders
        // (see comment above). Prefer property coords → requester coords →
        // state fallback. Backend applies a 25-mi Haversine filter when either
        // coord pair is present.
        const propertyLat = finiteOrNull(transaction?.propertyLatitude);
        const propertyLng = finiteOrNull(transaction?.propertyLongitude);
        let requesterLat = null;
        let requesterLng = null;
        let stateParam;
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          requesterLat = finiteOrNull(u.latitude);
          requesterLng = finiteOrNull(u.longitude);
          if (transaction?.propertyState) {
            stateParam = transaction.propertyState;
          } else {
            stateParam = u.licenseState || u.stateCode || u.state || undefined;
          }
        } catch {
          // localStorage parse failure — fall through with everything unset.
        }
        // `calendarExpandedSearch=true` drops the coord fallback — requester
        // asked to see providers outside the 25-mi radius. Typing a
        // 3-char+ query implicitly does the same: name-based lookup only
        // makes sense state-wide, otherwise the requester can't find the
        // SP they typed if that SP happens to be > 25 mi away.
        const nameActive = providerNameQuery.length > 0;
        const useCoords = !calendarExpandedSearch && !nameActive && propertyLat != null && propertyLng != null;
        const useRequesterCoords = !calendarExpandedSearch && !nameActive && !useCoords && requesterLat != null && requesterLng != null;
        const resp = await apiClient.get(API_ENDPOINTS.serviceProviders, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            serviceType: apiParam,
            size: 100,
            sort: 'rating,desc',
            ...(useCoords ? { referenceLat: propertyLat, referenceLng: propertyLng } : {}),
            ...(useRequesterCoords ? { referenceLat: requesterLat, referenceLng: requesterLng } : {}),
            ...(!useCoords && !useRequesterCoords && stateParam ? { state: stateParam } : {}),
            ...(nameActive ? { name: providerNameQuery } : {}),
          },
        });
        if (cancelled) return;
        const list = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        setProviderList(list);
      } catch {
        if (!cancelled) setProviderList([]);
      } finally {
        if (!cancelled) setProviderListLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showProviderPickerModal, calendarForm.serviceType, calendarExpandedSearch, providerNameQuery, vendorServiceTypeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectProvider = (provider) => {
    const id = provider.id || provider.providerId || provider.serviceProviderId || provider.userId || provider.firebaseUid || null;
    setSelectedProviderId(id);
    setCalendarForm(prev => ({
      ...prev,
      providerName:  displayNameForProvider(provider),
      providerPhone: provider.phoneNumber || provider.phone || prev.providerPhone,
      providerEmail: provider.email || prev.providerEmail,
    }));
    setShowProviderPickerModal(false);
  };

  // Submit calendar entry to API
  const handleCreateCalendarEntry = async () => {
    setCalendarEntryError('');

    const isCustom = timelineEntryType === 'custom';
    const isService = timelineEntryType === 'service';

    // Validate required fields
    if (isCustom) {
      if (!calendarForm.title.trim()) return setCalendarEntryError('Entry title is required.');
      if (!calendarForm.entryType) return setCalendarEntryError('Entry type is required.');
      if (!calendarForm.scheduledDate) return setCalendarEntryError('Date is required.');
    }
    if (isService) {
      if (!calendarForm.serviceType) return setCalendarEntryError('Service type is required.');
      // Provider Name is optional — matches the dashboard "Create Service
      // Request" open-SR flow (2026-09-08). When left blank in External
      // Vendor + Your Network mode, the SR still gets created but with
      // providerId=null (lands in AWAITING_ASSIGNMENT); the requester
      // assigns a provider later from the timeline's Assign SP button.
      if (!calendarForm.scheduledDate) return setCalendarEntryError('Date is required.');
      if (serviceProviderType === 'external' && !calendarForm.externalAcknowledged) {
        return setCalendarEntryError('Please acknowledge that this provider works externally.');
      }
    }

    try {
      setIsCreatingEntry(true);
      const firebaseToken = await getFirebaseToken();
      const authHeaders = { Authorization: `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };

      // Custom entries are saved to the Calendar service (calendar_events) via
      // POST /api/v1/calendar/events — the same endpoint AgentCalendarPage uses —
      // not to the transaction timeline. Field names match the backend
      // CreateEventRequest (entryType -> eventType, notes -> description).
      // Service Request entries keep the existing transaction-timeline behavior below.
      if (isCustom) {
        const durationLabel = DURATION_MAP[Number(calendarForm.duration)] || '1 hour';
        // Date-only: the form has no time input (calendarForm.scheduledTime is
        // a hidden '23:59' placeholder). Converting that placeholder to UTC
        // pushed the date to the NEXT day for every US zone, and
        // calendar-service then dropped the time (no scheduledTimeProvided),
        // leaving the event stored a day late. A null time keeps the picked
        // date as-is, which every reader (eventLocalDateKey /
        // formatEventForViewer) renders literally.
        const { scheduledDate: utcDate, scheduledTime: utcTime } =
          toUtcWallClock(calendarForm.scheduledDate, null);
        const eventPayload = {
          title: calendarForm.title.trim(),
          scheduledDate: utcDate,
          scheduledTime: utcTime || null,
          timeZone: 'UTC',
          duration: durationLabel,
          eventType: calendarForm.entryType || 'other',
          allDay: durationLabel === 'All day',
          category: 'custom',
          ...(calendarForm.notes?.trim() && { description: calendarForm.notes.trim() }),
          ...(calendarForm.location?.trim() && { location: calendarForm.location.trim() }),
          ...(id && { transactionId: id }),
          ...(selectedMilestoneId && { milestoneId: selectedMilestoneId }),
        };
        const eventRes = await apiClient.post(`${API_BASE_URL}/api/v1/calendar/events`, eventPayload, { headers: authHeaders });
        const createdEvent = eventRes.data?.data;
        // Show it on the timeline immediately (auto-placed by date) — it also
        // appears on the Calendar page since it lives in calendar_events.
        if (createdEvent?.eventId) {
          setCalendarEvents(prev => [...prev, createdEvent]);
        }
        setShowTimelineModal(false);
        return;
      }

      // Service Request entries are also saved to the Calendar service
      // (calendar_events) so they show on the Calendar page and auto-place on the
      // transaction Timeline — the same path as Custom entries. They carry
      // category='service' + provider info (name/email as invitees). The vendor
      // "job" (create-service-request) is still fired for network providers.
      const durationLabel = DURATION_MAP[Number(calendarForm.duration)] || '1 hour';
      const serviceTitle = SERVICE_ENTRY_LABELS[calendarForm.serviceType] || calendarForm.serviceType;
      // "Other" + a typed name: sent as customServiceType while serviceType stays
      // canonical "Other" (SR ↔ event correlation keys off it). Only the visible
      // event title uses the custom name — same as CreateServiceRequestPage.
      const trimmedCustomServiceType = calendarForm.serviceType.toLowerCase() === 'other'
        ? (calendarForm.customServiceType || '').trim()
        : '';
      const serviceEventTitle = getServiceTypeLabel({ serviceType: serviceTitle, customServiceType: trimmedCustomServiceType });
      const inviteeList = [];
      if (calendarForm.providerName.trim() || calendarForm.providerEmail.trim() || calendarForm.providerPhone.trim()) {
        inviteeList.push({
          ...(calendarForm.providerName.trim() && { name: calendarForm.providerName.trim() }),
          ...(calendarForm.providerEmail.trim() && { email: calendarForm.providerEmail.trim() }),
          ...(calendarForm.providerPhone.trim() && { phone: calendarForm.providerPhone.trim() }),
        });
      }
      // Date-only, same as the SR's requestedDate below (toUtcInstant(date,
      // null)) — see the custom-entry branch above for why the hidden '23:59'
      // placeholder must not be converted (it stored the event a day late).
      const { scheduledDate: utcServiceDate, scheduledTime: utcServiceTime } =
        toUtcWallClock(calendarForm.scheduledDate, null);
      const serviceEventPayload = {
        title: serviceEventTitle,
        scheduledDate: utcServiceDate,
        scheduledTime: utcServiceTime || null,
        timeZone: 'UTC',
        duration: durationLabel,
        eventType: calendarForm.serviceType,
        allDay: durationLabel === 'All day',
        category: 'service',
        providerType: serviceProviderType === 'transaction' ? 'transaction_party' : 'external_vendor',
        ...(calendarForm.notes?.trim() && { description: calendarForm.notes.trim() }),
        ...(id && { transactionId: id }),
        ...(selectedMilestoneId && { milestoneId: selectedMilestoneId }),
        ...(inviteeList.length && { invitees: inviteeList }),
      };
      const serviceEventRes = await apiClient.post(`${API_BASE_URL}/api/v1/calendar/events`, serviceEventPayload, { headers: authHeaders });
      const createdServiceEvent = serviceEventRes.data?.data;
      if (createdServiceEvent?.eventId) {
        setCalendarEvents(prev => [...prev, createdServiceEvent]);
      }

      // Always create a formal service request when adding a service entry,
      // regardless of provider source. "Your network" with a pick → SR routed
      // to that SP. "Your network" left blank OR "Other" (free-text external
      // provider) → open SR (providerId omitted, backend lands it in
      // AWAITING_ASSIGNMENT) so the timeline row surfaces the status pill +
      // "Assign SP" + "View Details" + "Mark as Transaction Document"
      // affordances the same way. Without the SR the timeline row would only
      // get the calendar event, and gating predicates on linkedSr/linkedSrStatus
      // in TransactionTimeline hide the status pill and View Details button.
      // Free-text name/phone/email in "Other" mode still lives on the
      // calendar-event invitees for external contact reference; the SR
      // itself is provider-less. Matches the dashboard "Create Service
      // Request" open-SR flow 2026-09-08.
      {
        // Profile-timezone wall-clock parse, converted to a UTC instant. See
        // the matching comment on handleCreateCalendarEvent's requested* ISO
        // construction above.
        const requestedDateISO = toUtcInstant(calendarForm.scheduledDate, null);
        const parsedEstimate = calendarForm.estimatedAmount !== ''
          ? Number(calendarForm.estimatedAmount)
          : null;
        const trimmedNotes = calendarForm.notes?.trim() || '';
        // Atomic back-link via user-service (2026-09-11): calendarEventId in
        // the create payload — server-side link, no fragile client PUT.
        const srRes = await apiClient.post(API_ENDPOINTS.createServiceRequest, {
          serviceType:     serviceTitle,
          ...(trimmedCustomServiceType && { customServiceType: trimmedCustomServiceType }),
          propertyAddress: calendarForm.location?.trim() || '',
          requestedDate:   requestedDateISO,
          urgencyLevel:    calendarForm.urgencyLevel || 'NORMAL',
          transactionId:   id,
          ...(selectedProviderId && { providerId: selectedProviderId }),
          ...(trimmedNotes && { description: trimmedNotes }),
          ...(parsedEstimate !== null && Number.isFinite(parsedEstimate) && parsedEstimate >= 0 && { estimatedAmount: parsedEstimate }),
          ...(createdServiceEvent?.eventId && { calendarEventId: createdServiceEvent.eventId }),
        }, { headers: authHeaders });
        const createdSr = srRes.data?.data || srRes.data || null;
        const srId = createdSr?.id || createdSr?.requestId;
        // Mirror the newly-server-linked SR id onto the local calendar-events
        // state so tier-1 correlation resolves immediately (server persistence
        // has already happened; this is just the client cache).
        if (srId && createdServiceEvent?.eventId) {
          setCalendarEvents(prev => prev.map(e => (
            e.eventId === createdServiceEvent.eventId ? { ...e, serviceRequestId: srId } : e
          )));
        }
        // Optimistic-insert the fresh SR into transactionServiceRequests
        // so the timeline pill renders immediately, without waiting for
        // the next /sent-service-requests refetch. Matches the reassign
        // path in handleAssignServiceProvider.
        if (srId) {
          setTransactionServiceRequests(prev => {
            const already = prev.some(sr =>
              (sr.id || sr.requestId || sr.serviceRequestId) === srId);
            if (already) return prev;
            // Fallback status only fires when the POST response didn't
            // include a status — pick AWAITING_ASSIGNMENT for the open-SR
            // case (no provider picked) so the timeline pill reads
            // "Provider Not Assigned" immediately instead of flashing
            // "Pending Acceptance". createdSr from a successful response
            // always carries the authoritative backend-picked status.
            const optimistic = createdSr || {
              id: srId,
              status: selectedProviderId ? 'PENDING' : 'AWAITING_ASSIGNMENT',
              providerId: selectedProviderId || null,
              serviceType: serviceTitle,
              ...(trimmedCustomServiceType && { customServiceType: trimmedCustomServiceType }),
              requestedDate: requestedDateISO,
              requestedTime: null,
              transactionId: id,
            };
            return [optimistic, ...prev];
          });
        }
      }

      setShowTimelineModal(false);
    } catch (error) {
      console.error('Error creating calendar entry:', error);
      setCalendarEntryError(error.response?.data?.message || 'Failed to create calendar entry. Please try again.');
    } finally {
      setIsCreatingEntry(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="txn-detail-container">
        <div className="txn-layout-wrapper">
          <div className="txn-layout-container">
            <div className="txn-main-area">
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="txn-page-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                <h2>Loading Transaction...</h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no transaction found
  if (!transaction) {
    return (
      <div className="txn-detail-container">
        <div className="txn-layout-wrapper">
          <div className="txn-layout-container">
            <div className="txn-main-area">
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                <h2>Transaction Not Found</h2>
                <p>The transaction you're looking for doesn't exist or you don't have access to it.</p>
                <button className="txn-back-button" onClick={handleBackToTransactions} style={{ marginTop: '20px' }}>
                  <span>←</span>
                  <span>Back to Transactions</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeRole = (getActiveRoleFromStorage() || '').toUpperCase();
  const canViewHistory = activeRole === 'AGENT' || activeRole === 'TRANSACTION_COORDINATOR'
    || ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes(activeRole);
  // Backend-driven: granted to AGENT + TRANSACTION_COORDINATOR (+ ADMIN via wildcard)
  // in ContextPermissionResolver. Scoped to the currently-active role via
  // rolePermissions[] so a multi-role user (e.g. Agent+Seller) who has
  // switched to Seller mode does not see the inbox.
  const canViewInbox = hasPermissionForRole(activeRole, 'inbox.view');

  return (
    <div className="txn-detail-container">

      {/* Main Layout Container */}
      <div className="txn-layout-wrapper">
      <div className="txn-layout-container">
        {/* Sidebar */}
        <div className="txn-sidebar">
          <div className="txn-sidebar-header">
            <button className="txn-back-button-small" onClick={handleBackToTransactions}>
              <span>←</span>
              <span>Back</span>
            </button>
            <div className="txn-sidebar-title">{transaction?.transactionName || 'Transaction'}</div>
            <div className="txn-sidebar-address">{getFullAddress()}</div>
            <div className="txn-sidebar-status-row">
              <span className={`txn-sidebar-status-badge ${getStatusBadgeClass(transaction.status)}`}>{formatStatus(transaction.status)}</span>
              <span className="txn-sidebar-phase">{getTransactionType()}</span>
              {transaction.offerAccepted && (
                <span className="txn-sidebar-status-badge txn-badge-offer-accepted">Offer Accepted</span>
              )}
            </div>
          </div>

          <div className="txn-sidebar-nav">
            <div
              className={`txn-sidebar-nav-item ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => { setActiveTab('timeline'); fetchMilestones(); }}
            >
              <div className="txn-sidebar-nav-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
                <span>Timeline</span>
              </div>
            </div>
            <div
              className={`txn-sidebar-nav-item ${activeTab === 'docs' ? 'active' : ''}`}
              onClick={() => setActiveTab('docs')}
            >
              <div className="txn-sidebar-nav-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span>Documents</span>
              </div>
              <div className="txn-sidebar-nav-count">{documentsCount}</div>
            </div>
            {/* Disclosure tab — listing/seller agent, or a Transaction Coordinator on a
                listing-side transaction (mirrors the Offers tab's TC handling above —
                the backend already resolves LISTING_AGENT/CO_LISTING_AGENT/TC the same
                way for disclosure-share endpoints). A TC on a buying-side transaction
                still doesn't see this tab — there's no seller disclosure to manage there.
                Never visible to buyers, buyer's agents, sellers themselves, or any other role. */}
            {(() => {
              const userRole = getUserRoleInTransaction(transaction, participants);
              const txnSideUp = (transaction?.side || transaction?.type || '').toUpperCase();
              const isListingSide = txnSideUp === 'LISTING' || txnSideUp === 'SALE' || txnSideUp === 'SELLING';
              const canViewDisclosures = userRole === 'LISTING_AGENT' || (userRole === 'TRANSACTION_COORDINATOR' && isListingSide);
              if (!canViewDisclosures) return null;
              return (
                <div
                  className={`txn-sidebar-nav-item ${activeTab === 'disclosure' ? 'active' : ''}`}
                  onClick={() => setActiveTab('disclosure')}
                >
                  <div className="txn-sidebar-nav-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 9.7 8 10 4.5-.3 8-5 8-10V6z"/><path d="m9 12 2 2 4-4"/></svg>
                    <span>Disclosures</span>
                  </div>
                </div>
              );
            })()}
            <div
              className={`txn-sidebar-nav-item ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              <div className="txn-sidebar-nav-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <span>Parties</span>
              </div>
              <div className="txn-sidebar-nav-count">{participants.length}</div>
            </div>
            {/* Offers tab — Listing Agent, Seller, or Transaction Coordinator on a
                listing-side transaction. Mirrors mobile TransactionDetailScreen.tsx:
                TCs get agent-level access on Offers (upload offer packages, initiate
                Send for Signature) since the backend already resolves LISTING_AGENT +
                SELLER as signers regardless of who invoked it. A TC on a buying-side
                transaction still sees Selected Properties, since there's no seller
                offer package to manage there. Broker (incl. Manager/Admin) and
                Brokerage viewers also get Offers on a listing-side transaction —
                read-only, since SellerOffers only enables actions for
                isListingAgent/isSeller. Selected Properties is buying-side only: a
                listing-side transaction never shows it. */}
            {(() => {
              const userRole = getUserRoleInTransaction(transaction, participants);
              const txnSideUp = (transaction?.side || transaction?.type || '').toUpperCase();
              const isListingSide = txnSideUp === 'LISTING' || txnSideUp === 'SALE' || txnSideUp === 'SELLING';
              const isBrokerOrBrokerageViewer = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE'].includes(activeRole);
              const isListingOrSeller = userRole === 'LISTING_AGENT' || userRole === 'SELLER' || (userRole === 'TRANSACTION_COORDINATOR' && isListingSide)
                || (isBrokerOrBrokerageViewer && isListingSide);
              if (isListingOrSeller) {
                return (
                  <div
                    className={`txn-sidebar-nav-item ${activeTab === 'offers' ? 'active' : ''}`}
                    onClick={() => setActiveTab('offers')}
                  >
                    <div className="txn-sidebar-nav-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                      <span>Offers</span>
                    </div>
                    {offerCount > 0 && (
                      <div className="txn-sidebar-nav-count">{offerCount}</div>
                    )}
                  </div>
                );
              }
              if (isListingSide) return null;
              return (
                <div
                  className={`txn-sidebar-nav-item ${activeTab === 'properties' ? 'active' : ''}`}
                  onClick={() => setActiveTab('properties')}
                >
                  <div className="txn-sidebar-nav-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    <span>Selected Properties</span>
                  </div>
                  {propertiesCount > 0 && (
                    <div className="txn-sidebar-nav-count">{propertiesCount}</div>
                  )}
                </div>
              );
            })()}
            {canViewInbox && (
              <div
                className={`txn-sidebar-nav-item ${activeTab === 'inbox' ? 'active' : ''}`}
                onClick={() => setActiveTab('inbox')}
              >
                <div className="txn-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>Inbox</span>
                </div>
                {inboxCount > 0 && (
                  <div className="txn-sidebar-nav-count">{inboxCount}</div>
                )}
              </div>
            )}
            {canViewHistory && (
              <div
                className={`txn-sidebar-nav-item ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => { setPendingAuditChip(null); setActiveTab('history'); }}
              >
                <div className="txn-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
                  <span>Transaction Log</span>
                </div>
              </div>
            )}
            {/* <div
              className={`txn-sidebar-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <div className="txn-sidebar-nav-label">
                <span>⚙️</span>
                <span>Settings</span>
              </div>
            </div> */}
          </div>

          {/* Cancel Transaction — any agent participant, not already cancelled or closed */}
          {getAccessControl(transaction, participants).isAgent() && transaction.status !== 'CANCELLED' && transaction.status !== 'CLOSED' && (
            <div className="txn-sidebar-cancel-section">
              <button
                className="txn-cancel-transaction-btn"
                onClick={() => { setCancelReason(''); setCancelError(''); setShowCancelModal(true); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span>Cancel Transaction</span>
              </button>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="txn-main-area">
          {/* Payment status banner — only on closed transactions. Hidden on
              the Settings tab since Settings is a page-level view. See
              derivePaymentBannerState for the state → label mapping.
              Semantic colouring: state.color + state.bg come from the state
              map; the banner frame and button both consume them so the whole
              banner reads as "payment alert" (not a neutral brand box). */}
          {transaction?.status?.toUpperCase() === 'CLOSED' && activeTab !== 'settings' && (() => {
            const state = derivePaymentBannerState(
              transaction.paymentStatus,
              transaction.refundedAmountCents,
            );
            return (
              <div
                className="txn-payment-banner"
                style={{
                  backgroundColor: state.bg,
                  borderColor: state.color,
                }}
                role={state.showPayNow ? 'alert' : undefined}
              >
                <div className="txn-payment-banner-label">
                  <svg
                    className="txn-payment-banner-icon"
                    style={{ color: state.color }}
                    width="18" height="18" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>{state.label}</span>
                </div>
                {state.showPayNow && (
                  <button
                    type="button"
                    onClick={handleBannerPayNow}
                    disabled={isBannerPaying}
                    className="txn-payment-banner-btn"
                    style={{ backgroundColor: state.color }}
                  >
                    {isBannerPaying ? 'Processing…' : state.payLabel}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Timeline Content */}
          {activeTab === 'timeline' && (
            <TransactionTimeline
              transaction={transaction}
              milestones={milestones}
              participants={participants}
              isLoading={isMilestonesLoading}
              isApplyingTemplate={isApplyingTemplate}
              hasProperties={transactionHasProperties}
              interestedPropertyId={firstInterestedPropertyId}
              onListingAgreementParsed={setParsedListingAgreement}
              onShowCalendarModal={(milestoneId) => { resetCalendarForm(); setSelectedMilestoneId(milestoneId || null); setShowTimelineModal(true); setTimelineEntryType('custom'); setServiceProviderType('external'); }}
              onCompleteMilestone={handleCompleteMilestone}
              onSkipMilestone={handleSkipMilestone}
              calendarEntries={calendarEntries}
              calendarEvents={calendarEvents}
              onProspectStageComplete={handleProspectStageComplete}
              onProspectStageSkip={handleProspectStageSkip}
              onDeleteCalendarEntry={handleDeleteCalendarEntry}
              onCompleteCalendarEvent={handleCompleteCalendarEvent}
              onEditStageDate={handleEditStageDate}
              onEditCalendarEventDate={handleEditCalendarEventDate}
              onAssignServiceProvider={handleAssignServiceProvider}
              onSearchServiceProviders={handleSearchServiceProviders}
              sentSrsLoaded={sentSrsLoaded}
              onSrUpdated={(updated) => {
                if (!updated?.id) return;
                setTransactionServiceRequests((prev) => prev.map((sr) => {
                  const sid = sr.id || sr.requestId || sr.serviceRequestId;
                  return sid === updated.id ? { ...sr, ...updated } : sr;
                }));
              }}
              srStatusById={srStatusById}
              srStatusByProviderEmail={srStatusByProviderEmail}
              srByProviderEmail={srByProviderEmail}
              srAttachmentsById={srAttachmentsById}
              srByServiceType={srByServiceType}
              srsByServiceType={srsByServiceType}
              srById={srById}
              accessControl={getAccessControl(transaction, participants)}
              onRefreshMilestones={fetchMilestones}
              onStartActiveListing={handleStartActiveListing}
              onActiveListingStageComplete={handleActiveListingStageComplete}
              onActiveListingStageSkip={handleActiveListingStageSkip}
              onOpenPurchaseAgreementPicker={openPurchaseAgreementSelectPicker}
              purchaseAgreementRefreshToken={paRefreshToken}
              activeListingStagesReady={activeListingStagesReady}
              activeBuyingStagesReady={activeBuyingStagesReady}
              isConvertingToActiveListing={isConvertingToActiveListing}
              activeListingMlsIdInput={activeListingMlsIdInput}
              onActiveListingMlsIdInputChange={setActiveListingMlsIdInput}
              isSavingMlsId={isSavingMlsId}
              onConvertToClosed={handleConvertToClosed}
              isConvertingToClosed={isConvertingToClosed}
              onSetAcceptanceDate={handleSetAcceptanceDate}
              isSettingAcceptanceDate={isSettingAcceptanceDate}
            />
          )}

          {/* Documents Content */}
          {activeTab === 'docs' && (
            <TransactionDocuments
              transaction={transaction}
              participants={participants}
              accessControl={getAccessControl(transaction, participants)}
              onDocumentsChange={(count) => setDocumentsCount(count)}
              onNavigateToAuditTrail={canViewHistory ? (chipKey) => {
                setPendingAuditChip(chipKey || 'all');
                setActiveTab('history');
              } : undefined}
            />
          )}

          {/* Disclosure Content — mirrors the sidebar tab's access check above. Double-check
              the role here too so a stale activeTab value (e.g. from location.state) can
              never render the tab for a user who shouldn't see it. */}
          {activeTab === 'disclosure' && (() => {
            const userRole = getUserRoleInTransaction(transaction, participants);
            const txnSideUp = (transaction?.side || transaction?.type || '').toUpperCase();
            const isListingSide = txnSideUp === 'LISTING' || txnSideUp === 'SALE' || txnSideUp === 'SELLING';
            const canViewDisclosures = userRole === 'LISTING_AGENT' || (userRole === 'TRANSACTION_COORDINATOR' && isListingSide);
            return canViewDisclosures ? <DisclosureTab transaction={transaction} /> : null;
          })()}

          {/* Participants Content */}
          {activeTab === 'chat' && (
            <TransactionChatParties
              transaction={transaction}
              participants={participants}
              accessControl={getAccessControl(transaction, participants)}
              onParticipantsChange={(updated) => setParticipants(updated)}
            />
          )}

          {/* Properties Content */}
          {activeTab === 'properties' && (
            <TransactionProperties
              transaction={transaction}
              accessControl={getAccessControl(transaction, participants)}
              onPropertiesLoaded={(count, firstId) => { setTransactionHasProperties(count > 0); setFirstInterestedPropertyId(firstId); }}
            />
          )}

          {/* Offers Content — Listing Agent, Seller, or Transaction Coordinator.
              isListingAgent mirrors mobile's isAgent={isEffectiveAgent || isTC}
              passed into OffersTab — TCs get the same upload / Send for Signature
              access as the listing agent (backend resolves LISTING_AGENT + SELLER
              as signers regardless of who invoked it). */}
          {activeTab === 'offers' && (() => {
            const userRole = getUserRoleInTransaction(transaction, participants);
            const txnSideUp = (transaction?.side || transaction?.type || '').toUpperCase();
            const isListingSide = txnSideUp === 'LISTING' || txnSideUp === 'SALE' || txnSideUp === 'SELLING';
            return (
              <SellerOffers
                transaction={transaction}
                participants={participants}
                isListingAgent={userRole === 'LISTING_AGENT' || (userRole === 'TRANSACTION_COORDINATOR' && isListingSide)}
                isSeller={userRole === 'SELLER'}
                onOfferCount={(count) => setOfferCount(count)}
              />
            );
          })()}

          {/* Inbox Content */}
          {activeTab === 'inbox' && canViewInbox && (
            <TransactionInbox
              transaction={transaction}
              onEmailCountChange={(count) => setInboxCount(count)}
            />
          )}

          {/* Audit Trail Content */}
          {activeTab === 'history' && canViewHistory && (
            <TransactionHistory transaction={transaction} initialChip={pendingAuditChip} />
          )}

          {/* Settings Content */}
          {activeTab === 'settings' && (
            <div className="txn-content-section">
              <div className="txn-content-header">
                <h2 className="txn-content-title">Settings</h2>
              </div>
              <p className="txn-tab-placeholder">Settings will be displayed here</p>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Contract Date Confirmation Modal (shown before converting Prospect → Active) */}
      {showContractDateModal && (
        <div className="txn-modal-overlay" onClick={() => { setShowContractDateModal(false); setPendingConversionStageId(null); }}>
          <div className="txn-modal txn-contract-date-modal" onClick={(e) => e.stopPropagation()}>
            {/* Icon + heading */}
            <div className="txn-cd-modal-header">
              <div className="txn-cd-modal-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <h2 className="txn-cd-modal-title">Confirm Contract Date</h2>
              <p className="txn-cd-modal-subtitle">
                Please confirm the date the RPA was signed.<br />
                This will convert the transaction to <strong>Active</strong>.
              </p>
            </div>

            {/* Date input */}
            <div className="txn-cd-modal-body">
              <label className="txn-cd-date-label">Contract Date (RPA Signed)</label>
              <input
                type="date"
                className="txn-cd-date-input"
                value={contractDate}
                onChange={(e) => setContractDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="txn-cd-date-hint">You can change this date before confirming.</p>
            </div>

            {/* Footer actions */}
            <div className="txn-cd-modal-footer">
              <button
                className="txn-btn txn-btn-secondary txn-cd-cancel-btn"
                onClick={() => { setShowContractDateModal(false); setPendingConversionStageId(null); }}
              >
                Cancel
              </button>
              <button
                className="txn-btn txn-btn-primary txn-cd-confirm-btn"
                onClick={handleConfirmContractDate}
                disabled={!contractDate}
              >
                ✓&nbsp; Confirm &amp; Convert to Active
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Intro Modal — shown once after Prospect → Active conversion */}
      {showTimelineIntroModal && (
        <div className="txn-modal-overlay txn-ti-overlay" onClick={() => setShowTimelineIntroModal(false)}>
          <div className="txn-modal txn-timeline-intro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-ti-icon-wrap">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <polyline points="9 16 11 18 15 14"/>
              </svg>
            </div>
            <h2 className="txn-ti-title">Review your transaction timelines</h2>
            <p className="txn-ti-body">
              Transaction timelines have been generated. Please review and make any required changes using the edit option on each date.
            </p>
            <button
              className="txn-ti-got-it-btn"
              onClick={() => setShowTimelineIntroModal(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Listing Agreement required — gates Convert to Active until the
          Listing Agreement Signed stage is marked COMPLETED. Same visual
          shape as the No-Docs modal below. */}
      {showLaRequiredModal && (
        <div className="txn-modal-overlay txn-ti-overlay" onClick={() => setShowLaRequiredModal(false)}>
          <div className="txn-modal txn-no-docs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-no-docs-icon-wrap">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="9" y1="13" x2="15" y2="13"/>
                <line x1="9" y1="17" x2="13" y2="17"/>
              </svg>
            </div>
            <h2 className="txn-no-docs-title">Mark as signed listing agreement first</h2>
            <p className="txn-no-docs-body">
              Please mark "Listing Agreement Signed" as completed before converting to Active.
            </p>
            <button
              className="txn-no-docs-btn"
              onClick={() => setShowLaRequiredModal(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* No Documents Attached Modal — shown when converting a listing to Active
          (seller_rpa_signed marked complete) finds no DocuSign documents to pick from.
          Mirrors the Timeline Intro Modal's simple icon/title/body/button layout. */}
      {showNoDocsModal && (
        <div className="txn-modal-overlay txn-ti-overlay" onClick={() => setShowNoDocsModal(false)}>
          <div className="txn-modal txn-no-docs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-no-docs-icon-wrap">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="9" y1="13" x2="15" y2="13"/>
                <line x1="9" y1="17" x2="13" y2="17"/>
              </svg>
            </div>
            <h2 className="txn-no-docs-title">No Documents Attached</h2>
            <p className="txn-no-docs-body">
              No documents are attached to this listing yet. Upload the signed contract and any counter offers before converting to Active.
            </p>
            <button
              className="txn-no-docs-btn"
              onClick={() => setShowNoDocsModal(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Cancel Transaction Confirmation Modal */}
      {showCancelModal && (
        <div className="txn-modal-overlay" onClick={() => !isCancelling && setShowCancelModal(false)}>
          <div className="txn-modal txn-cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-cancel-modal-header">
              <div className="txn-cancel-modal-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h2 className="txn-cancel-modal-title">Cancel Transaction?</h2>
              <p className="txn-cancel-modal-subtitle">
                This will mark the transaction as <strong>Fell Through</strong>. This action cannot be undone.
              </p>
            </div>

            <div className="txn-modal-body">
              <div className="txn-form-group">
                <label className="txn-form-label">
                  Reason for Cancellation <span className="txn-form-required">*</span>
                </label>
                <textarea
                  className={`txn-form-input ${cancelError ? 'txn-input-error' : ''}`}
                  rows="3"
                  placeholder="e.g., Buyer withdrew offer, financing fell through, seller changed mind..."
                  value={cancelReason}
                  onChange={(e) => { setCancelReason(e.target.value); setCancelError(''); }}
                  disabled={isCancelling}
                />
                {cancelError && (
                  <p className="txn-cancel-error">{cancelError}</p>
                )}
              </div>
            </div>

            <div className="txn-modal-footer">
              <button
                className="txn-btn txn-btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                Keep Transaction
              </button>
              <button
                className="txn-btn txn-btn-primary"
                onClick={handleCancelTransaction}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add To Timeline Modal */}
      {showTimelineModal && (
        <div className="txn-modal-overlay" onClick={() => setShowTimelineModal(false)}>
          <div className="txn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-modal-header">
              <div className="txn-modal-header-top">
                <h2 className="txn-modal-title">Add To Timeline</h2>
                <button
                  className="txn-modal-close-btn"
                  onClick={() => setShowTimelineModal(false)}
                  aria-label="Close modal"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <p className="txn-modal-subtitle">What would you like to add to this transaction?</p>
            </div>

            <div className="txn-modal-body">
              {/* Entry Type Selection — Service Request is hidden for Buying
                  Prospect transactions (status=PENDING + side=BUYING) because
                  an agent shouldn't file an SR against a lead. Same predicate
                  the backend uses on the SR picker. */}
              {(() => {
                const isBuyingProspect = transaction?.status === 'PENDING' && transaction?.side === 'BUYING';
                return (
                  <div className="txn-entry-type-grid">
                    <div
                      className={`txn-entry-type-card ${timelineEntryType === 'custom' ? 'selected' : ''}`}
                      onClick={() => { setTimelineEntryType('custom'); setProviderSource('network'); setShowProviderPickerModal(false); }}
                    >
                      <div className="txn-entry-type-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
                      <div className="txn-entry-type-name">Custom Entry</div>
                      <div className="txn-entry-type-desc">Meeting, showing, consultation, or any custom event</div>
                    </div>
                    {!isBuyingProspect && (
                      <div
                        className={`txn-entry-type-card ${timelineEntryType === 'service' ? 'selected' : ''}`}
                        onClick={() => { setTimelineEntryType('service'); setProviderSource('network'); setShowProviderPickerModal(false); }}
                      >
                        <div className="txn-entry-type-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg></div>
                        <div className="txn-entry-type-name">Service Request</div>
                        <div className="txn-entry-type-desc">Inspector, appraiser, contractor, or other vendor</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Custom Entry Form */}
              {timelineEntryType === 'custom' && (
                <div className="txn-timeline-form-section">
                  <div className="txn-form-group">
                    <label className="txn-form-label">Entry Title <span className="txn-form-required">*</span></label>
                    <input type="text" className="txn-form-input" placeholder="e.g., Client Meeting, Property Showing" value={calendarForm.title} onChange={(e) => updateCalendarField('title', e.target.value)} />
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Entry Type <span className="txn-form-required">*</span></label>
                    <select className="txn-form-input" value={calendarForm.entryType} onChange={(e) => updateCalendarField('entryType', e.target.value)}>
                      <option value="">Select type...</option>
                      <option value="consultation">Lead Consultation</option>
                      <option value="showing">Property Showing</option>
                      <option value="meeting">Client Meeting</option>
                      <option value="open_house">Open House</option>
                      <option value="walkthrough">Final Walkthrough</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Due By Date <span className="txn-form-required">*</span></label>
                    <input type="date" className="txn-form-input" value={calendarForm.scheduledDate} onChange={(e) => updateCalendarField('scheduledDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Duration</label>
                    <select className="txn-form-input" value={calendarForm.duration} onChange={(e) => updateCalendarField('duration', e.target.value)}>
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                      <option value="180">3 hours</option>
                    </select>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Location</label>
                    <input type="text" className="txn-form-input" placeholder="Property address or meeting location" value={calendarForm.location} onChange={(e) => updateCalendarField('location', e.target.value)} />
                    <div className="txn-form-helper">Property address will be auto-filled if applicable</div>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Notes</label>
                    <textarea className="txn-form-input" rows="3" placeholder="Add any additional details or instructions..." value={calendarForm.notes} onChange={(e) => updateCalendarField('notes', e.target.value)}></textarea>
                  </div>
                </div>
              )}

              {/* Service Request Form */}
              {timelineEntryType === 'service' && (
                <div className="txn-timeline-form-section">
                  {/* Provider Type cards (Transaction Party / External Vendor) were
                      removed; serviceProviderType stays at its 'external' default,
                      so Service Type below lists the vendor service-type catalog.
                      The "External Vendor Communication" notice was removed too. */}
                  <div className="txn-form-group">
                    <label className="txn-form-label">Service Type <span className="txn-form-required">*</span></label>
                    <select className="txn-form-input" value={calendarForm.serviceType} onChange={(e) => setCalendarForm(prev => ({ ...prev, serviceType: e.target.value, customServiceType: '' }))}>
                      <option value="">Select service type...</option>
                      {serviceProviderType === 'transaction'
                        ? TRANSACTION_PARTY_SERVICE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))
                        : vendorServiceTypeOptions.map(opt => (
                            <option key={opt.id} value={opt.name}>{opt.name}</option>
                          ))}
                    </select>
                  </div>

                  {/* "Other" → requester names the service themselves (same as the
                      Create Service Request page) */}
                  {calendarForm.serviceType.toLowerCase() === 'other' && (
                    <div className="txn-form-group">
                      <label className="txn-form-label">Specify Service Type <span className="txn-form-optional">(optional)</span></label>
                      <input
                        type="text"
                        className="txn-form-input"
                        value={calendarForm.customServiceType}
                        maxLength={100}
                        placeholder="e.g. Solar Panel Inspection"
                        onChange={(e) => updateCalendarField('customServiceType', e.target.value)}
                      />
                    </div>
                  )}

                  {/* Provider Source — "Other" option was removed; the only
                      supported flow is "Your network" (state stays 'network'). */}
                  <div className="txn-form-group">
                    <label className="txn-form-label">Provider Source <span className="txn-form-required">*</span></label>
                    <div className="txn-provider-source-row">
                      <label className="txn-provider-source-option">
                        <input
                          type="radio"
                          name="providerSource"
                          value="network"
                          checked={providerSource === 'network'}
                          onChange={() => {
                            setProviderSource('network');
                            if (!selectedProviderId) {
                              setCalendarForm(prev => ({ ...prev, providerName: '' }));
                            }
                          }}
                        />
                        <span>Your network</span>
                      </label>
                    </div>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">
                      Provider Name <span className="txn-form-optional">(optional)</span>
                    </label>
                    {providerSource === 'network' ? (
                      // Picker-only in "Your network" mode. Click opens the
                      // Provider Picker modal listing all SPs (Companies +
                      // Individuals) for the chosen Service Type; free-text
                      // entry is disabled here.
                      calendarForm.providerName && selectedProviderId ? (
                        <div className="txn-provider-picked-row">
                          <div className="txn-provider-picked-name">{calendarForm.providerName}</div>
                          <button
                            type="button"
                            className="txn-provider-picked-change"
                            onClick={() => setShowProviderPickerModal(true)}
                            disabled={!calendarForm.serviceType}
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="txn-provider-select-btn"
                          onClick={() => setShowProviderPickerModal(true)}
                          disabled={!calendarForm.serviceType}
                          title={calendarForm.serviceType ? 'Select from your network' : 'Pick a Service Type first'}
                        >
                          <span>
                            {calendarForm.serviceType ? 'Select Provider' : 'Pick a Service Type first'}
                          </span>
                          <span className="txn-provider-select-chevron">▾</span>
                        </button>
                      )
                    ) : (
                      <input
                        type="text"
                        className="txn-form-input"
                        placeholder="Company or person name"
                        value={calendarForm.providerName}
                        onChange={(e) => updateCalendarField('providerName', e.target.value)}
                        autoComplete="off"
                      />
                    )}
                  </div>

                  {/* Phone + Email — shown for all provider types (matches mobile) */}
                  <div className="txn-form-row">
                    <div className="txn-form-group">
                      <label className="txn-form-label">Phone <span className="txn-form-optional">(optional)</span></label>
                      <input type="tel" className="txn-form-input" placeholder="(555) 123-4567" value={calendarForm.providerPhone} onChange={(e) => updateCalendarField('providerPhone', formatUSPhoneInput(e.target.value))} />
                    </div>
                    <div className="txn-form-group">
                      <label className="txn-form-label">Email <span className="txn-form-optional">(optional)</span></label>
                      <input type="email" className="txn-form-input" placeholder="contact@provider.com" value={calendarForm.providerEmail} onChange={(e) => updateCalendarField('providerEmail', e.target.value)} />
                    </div>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Due By Date <span className="txn-form-required">*</span></label>
                    <input type="date" className="txn-form-input" value={calendarForm.scheduledDate} onChange={(e) => updateCalendarField('scheduledDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Urgency Level</label>
                    <div className="txn-urgency-row">
                      {URGENCY_LEVELS.map(u => (
                        <button
                          key={u.value}
                          type="button"
                          className={`txn-urgency-pill txn-urgency-${u.value.toLowerCase()} ${calendarForm.urgencyLevel === u.value ? 'active' : ''}`}
                          onClick={() => updateCalendarField('urgencyLevel', u.value)}
                        >
                          {u.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Notes</label>
                    <textarea className="txn-form-input" rows="3" placeholder="Special instructions, areas of concern, etc." value={calendarForm.notes} onChange={(e) => updateCalendarField('notes', e.target.value)}></textarea>
                  </div>

                  <div className="txn-form-group">
                    <label className="txn-form-label">Estimated Amount <span className="txn-form-optional">(optional)</span></label>
                    <input
                      type="number"
                      className="txn-form-input"
                      placeholder="0.00"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={calendarForm.estimatedAmount}
                      onChange={(e) => updateCalendarField('estimatedAmount', e.target.value)}
                    />
                  </div>

                  {serviceProviderType === 'external' && (
                    <div className="txn-checkbox-group">
                      <input type="checkbox" className="txn-checkbox-input" checked={calendarForm.externalAcknowledged} onChange={(e) => updateCalendarField('externalAcknowledged', e.target.checked)} />
                      <label className="txn-checkbox-label">
                        I understand this provider will work externally. I'll contact them via phone/email and upload the final report to Documents when received.
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

            {calendarEntryError && (
              <div className="txn-modal-error" style={{ color: '#dc3545', background: '#fdf0f0', padding: '10px 16px', margin: '0 24px', borderRadius: '6px', fontSize: '14px' }}>
                {calendarEntryError}
              </div>
            )}

            <div className="txn-modal-footer">
              <button
                className="txn-btn txn-btn-secondary"
                onClick={() => setShowTimelineModal(false)}
                disabled={isCreatingEntry}
              >
                Cancel
              </button>
              <button
                className="txn-btn txn-btn-primary"
                onClick={handleCreateCalendarEntry}
                disabled={isCreatingEntry}
              >
                {isCreatingEntry ? 'Creating...' : 'Add To Timeline'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Parsing / converting overlay — mirrors mobile LoadingOverlay with convertingStep message */}
      {(isConverting || isLoadingListingPicker) && (
        <div className="txn-converting-overlay">
          <div className="txn-converting-box">
            <div className="txn-converting-spinner" />
            <p className="txn-converting-text">
              {convertingStep || (isLoadingListingPicker ? 'Loading documents…' : 'Working…')}
            </p>
          </div>
        </div>
      )}

      {/* Listing picker modal — mirrors mobile listingPickerVisible bottom sheet (TimelineTab.tsx lines 3524-3690) */}
      {listingPickerVisible && (
        <div className="lp-overlay" onClick={e => { if (e.target === e.currentTarget) { setListingPickerVisible(false); setPendingConversionStageId(null); } }}>
          <div className="lp-container">
            <div className="lp-header">
              <span className="lp-title">Add documents</span>
              <button className="lp-close-btn" onClick={() => { setListingPickerVisible(false); setPendingConversionStageId(null); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <p className="lp-subtitle">Select documents and tag each one's type. Tag exactly one as Purchase Agreement — counter offers ride along.</p>

            {/* Hidden file input for the Upload action */}
            <input
              ref={pickerUploadInputRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={handlePickerFileSelected}
            />

            {/* Search + Upload action (no mode toggle — the list is always shown) */}
            <div className="add-docs-toolbar">
              <div className="add-docs-search">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9E9E9E" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
                <input
                  type="text"
                  placeholder="Search documents"
                  value={pickerSearch}
                  onChange={e => setPickerSearch(e.target.value)}
                />
              </div>
              <button className="add-docs-upload-btn" type="button" onClick={() => pickerUploadInputRef.current?.click()}>
                + Upload
              </button>
            </div>

            <div className="lp-doc-list">
              {(() => {
                const q = pickerSearch.trim().toLowerCase();
                const matches = d => !q || (d.name || '').toLowerCase().includes(q);
                const txnDocs = listingPickerDocs.filter(matches);
                const formDocs = listingPickerFormsDocs.filter(matches);
                const renderRow = doc => {
                  const sel = listingPickerSelections[doc.documentId];
                  const isSelected = !!sel;
                  return (
                    <div key={doc.documentId} className="lp-doc-row">
                      <button className="lp-doc-tap" onClick={() => toggleListingDocSelection(doc)}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeLinecap="round">
                          {isSelected
                            ? <><rect x="3" y="3" width="18" height="18" rx="3" fill="#3d2d8f" stroke="#3d2d8f"/><polyline points="7 12 10.5 15.5 17 8.5" stroke="white" strokeWidth="2.5" fill="none"/></>
                            : <rect x="3" y="3" width="18" height="18" rx="3" stroke="#ccc" strokeWidth="2" fill="none"/>
                          }
                        </svg>
                        <div className="lp-doc-name-block">
                          <span className="lp-doc-name">{doc.name}</span>
                          {doc.userDisplayStatus && (
                            <span className="lp-doc-status-badge">{doc.userDisplayStatus}</span>
                          )}
                        </div>
                      </button>
                      {isSelected && (
                        <div className="lp-label-row">
                          {[['RPA', 'Purchase Agreement'], ['COUNTER_OFFER', 'Counter Offer'], ['LISTING_AGREEMENT', 'Listing Agreement'], ['OTHER', 'Other']].map(([val, display]) => (
                            <button
                              key={val}
                              className={`lp-label-chip${sel.label === val ? ' active' : ''}`}
                              onClick={() => setListingDocLabel(doc.documentId, val)}
                            >{display}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                };
                return (
                  <>
                    <button className="add-docs-section-header" onClick={() => setPickerTxnExpanded(p => !p)}>
                      <span className="add-docs-section-title">Transaction Documents</span>
                      <span className="add-docs-section-count">{txnDocs.length}</span>
                      <span className="add-docs-section-chevron">{pickerTxnExpanded ? '▲' : '▼'}</span>
                    </button>
                    {pickerTxnExpanded && (
                      <div className="add-docs-section-body">
                        {txnDocs.length === 0
                          ? <div className="lp-empty-hint">No transaction documents{q ? ' match your search' : ' yet'}.</div>
                          : txnDocs.map(renderRow)}
                      </div>
                    )}

                    <button className="add-docs-section-header" onClick={() => setPickerFormsExpanded(p => !p)}>
                      <span className="add-docs-section-title">Forms</span>
                      <span className="add-docs-section-count">{pickerFormsLoading ? '…' : formDocs.length}</span>
                      <span className="add-docs-section-chevron">{pickerFormsExpanded ? '▲' : '▼'}</span>
                    </button>
                    {pickerFormsExpanded && (
                      <div className="add-docs-section-body">
                        {pickerFormsLoading
                          ? <div className="lp-forms-loading"><span className="lp-forms-spinner" />Loading signed forms…</div>
                          : formDocs.length === 0
                            ? <div className="lp-empty-hint">No signed forms{q ? ' match your search' : ' available'}.</div>
                            : formDocs.map(renderRow)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {Object.keys(listingPickerSelections).length > 0 && (
              <div className="add-docs-selected-summary">
                {Object.keys(listingPickerSelections).length} document{Object.keys(listingPickerSelections).length === 1 ? '' : 's'} selected
              </div>
            )}

            <label className="lp-ack-row">
              <input
                type="checkbox"
                checked={listingPickerNoMoreAck}
                onChange={e => setListingPickerNoMoreAck(e.target.checked)}
              />
              <span className="lp-ack-text">
                I confirm there are no more documents to upload. All listing agreements, amendments, and supporting documents have been added above.
              </span>
            </label>

            <div className="lp-footer">
              {(() => {
                const anySigned = [...listingPickerDocs, ...listingPickerFormsDocs].some(d => d.isSigned)
                  || Object.values(listingPickerSelections).some(s => s.source === 'local');
                const hasRpa = Object.values(listingPickerSelections).some(s => s.label === 'RPA');
                // Select-only (from "Add a Document"): retain the selection + close — no
                // "no more documents" ack required, no parse. The next stage parses it.
                const continueDisabled = !hasRpa || !anySigned || (!pickerSelectOnly && !listingPickerNoMoreAck);
                return (
                  <>
                    {!hasRpa && (
                      <p className="lp-footer-hint">Tag one document as Purchase Agreement to continue.</p>
                    )}
                    <button
                      className="lp-submit-btn"
                      disabled={continueDisabled}
                      onClick={pickerSelectOnly ? submitUploadOnlyPicker : submitListingDocPicker}
                    >
                      {pickerSelectOnly ? 'Add selected' : 'Continue'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Conversion Review Modal — agent reviews AI-parsed timeline before converting to Active (buying + listing) */}
      {showConversionReviewModal && (
        <TransactionReviewModal
          visible={showConversionReviewModal}
          side={(transaction?.side || transaction?.type || '').toUpperCase()}
          parsed={parsedConversionResult}
          onReviewed={handleConversionReviewed}
          onCancel={() => {
            setShowConversionReviewModal(false);
            setParsedConversionResult(null);
          }}
        />
      )}

      {/* Cancel Service Request confirm — replaces window.confirm with the site's
          own Yes/No modal (same overlay/card/button classes as TransactionTimeline's
          "Confirm Documents Before Sending" modal, defined in TransactionTimeline.css
          and already loaded on this page since TransactionTimeline is always mounted
          alongside it). Yes triggers the same DELETE the browser confirm() used to
          gate; No just closes without calling the API. */}
      {cancelServiceConfirm && (
        <div className="txn-date-modal-overlay" onClick={handleCancelServiceConfirmNo}>
          <div className="txn-confirm-send-modal" onClick={e => e.stopPropagation()}>
            <div className="txn-confirm-send-title">Cancel Service Request</div>
            <p className="txn-confirm-send-body">
              This request has already been sent to a provider — cancelling
              will notify them. Are you sure?
            </p>
            <div className="txn-confirm-send-actions">
              <button className="txn-date-modal-cancel txn-confirm-send-btn" onClick={handleCancelServiceConfirmNo}>
                Keep
              </button>
              <button className="txn-date-modal-save txn-confirm-send-btn" onClick={handleCancelServiceConfirmYes}>
                Cancel Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Picker Modal — opens from Add-Timeline / Service Request
          "Select Provider" button. Lists all SPs (Companies + Individuals)
          for the currently chosen Service Type in grouped sections. */}
      {showProviderPickerModal && (
        <div className="txn-modal-overlay" onClick={() => setShowProviderPickerModal(false)}>
          <div className="txn-provider-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-provider-picker-header">
              {(providerSearchQuery || calendarExpandedSearch) ? (
                <button
                  type="button"
                  className="txn-provider-picker-back-btn"
                  onClick={() => {
                    // Revert to the initial nearby view — clears the search
                    // input and the "Show all providers" expand toggle. Mirror
                    // of AssignServiceProviderModal's provider-picker Back
                    // (PR #335): requester shouldn't have to close + re-open
                    // the whole flow to reach the "Invite a Provider" CTA.
                    setProviderSearchQuery('');
                    setDebouncedProviderSearch('');
                    setCalendarExpandedSearch(false);
                  }}
                  aria-label="Back to nearby providers"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  <span>Back</span>
                </button>
              ) : (
                <span />
              )}
              <h3 className="txn-provider-picker-title">
                Select {SERVICE_ENTRY_LABELS[calendarForm.serviceType] || calendarForm.serviceType || 'Provider'}
              </h3>
              <button
                type="button"
                className="txn-provider-picker-close"
                onClick={() => setShowProviderPickerModal(false)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="txn-provider-picker-body">
              {/* Search input — mirrors CreateServiceRequestPage's typeahead.
                  Typing 3+ chars flips the fetch to state-wide name-lookup
                  (drops the radius filter, sends `name=<query>` to backend).
                  Sits above the list so it's always visible even when the
                  radius result is empty. */}
              <div className="txn-provider-picker-search">
                <input
                  type="text"
                  className="txn-provider-picker-search-input"
                  placeholder="Search by name…"
                  value={providerSearchQuery}
                  onChange={(e) => setProviderSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {providerListLoading ? (
                <div className="txn-provider-picker-loading">Loading providers…</div>
              ) : providerList.length === 0 ? (
                <div className="txn-provider-picker-empty">
                  <p>
                    {providerNameQuery
                      ? `No providers found matching "${providerNameQuery}".`
                      : calendarExpandedSearch
                        ? 'No providers found — invite one manually.'
                        : 'No providers found near this property.'}
                  </p>
                  {!calendarExpandedSearch && !providerNameQuery && (
                    <button
                      type="button"
                      className="txn-provider-picker-expand-btn"
                      onClick={() => setCalendarExpandedSearch(true)}
                    >
                      Show all providers
                    </button>
                  )}
                  <button
                    type="button"
                    className="txn-provider-picker-invite-btn"
                    onClick={() => {
                      setShowProviderPickerModal(false);
                      // Mirror of AssignServiceProviderModal (PR #335):
                      // /invite-someone is the generic invite page that lets
                      // Agents/Brokers/TCs invite a SERVICE_PROVIDER (gated via
                      // INVITE_PERMISSIONS). prefillRole pre-selects the role
                      // dropdown so the requester lands on the provider form.
                      navigate('/invite-someone', { state: { prefillRole: 'SERVICE_PROVIDER' } });
                    }}
                  >
                    Invite a Provider
                  </button>
                </div>
              ) : (
                providerList.map((p, i) => {
                  const isCompany = (p.vendorType || '').toUpperCase() === 'COMPANY';
                  // Location fallback: city+state → state alone → licenseState.
                  // Distance badge shows when the backend is in radius mode.
                  const stateUpper = (p.state || p.licenseState || '').toUpperCase();
                  const locality = [p.city, stateUpper].filter(Boolean).join(', ');
                  // Guard against Number(null)===0 rendering "0.0 mi away".
                  const distanceLabel = (p.distanceMiles != null && Number.isFinite(Number(p.distanceMiles)))
                    ? `${Number(p.distanceMiles).toFixed(1)} mi away`
                    : null;
                  return (
                    <div
                      key={p.id || p.userId || i}
                      className="txn-provider-picker-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectProvider(p)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSelectProvider(p)}
                    >
                      <div className="txn-provider-picker-name-row">
                        <span className="txn-provider-picker-name">{displayNameForProvider(p)}</span>
                        {isCompany && (
                          <span className="txn-provider-picker-badge">COMPANY</span>
                        )}
                        {distanceLabel && (
                          <span className="txn-provider-picker-badge txn-provider-picker-distance">
                            {distanceLabel}
                          </span>
                        )}
                      </div>
                      {p.serviceType && (
                        <div className="txn-provider-picker-meta">{p.serviceType}</div>
                      )}
                      {locality && (
                        <div className="txn-provider-picker-meta txn-provider-picker-locality">
                          {locality}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {!calendarExpandedSearch && !providerNameQuery && providerList.length > 0 && !providerListLoading && (
                <button
                  type="button"
                  className="txn-provider-picker-expand-btn"
                  onClick={() => setCalendarExpandedSearch(true)}
                >
                  Not finding what you need? Show all providers
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stripe hosted-checkout modal — Pay-Now flow. Mirrors
          PaymentsPage: fetch clientSecret via POST /payments/charges/checkout-session,
          mount <EmbeddedCheckout /> inline, let Stripe host the UI. Payment
          confirmation lands via the Stripe webhook; this modal just closes. */}
      {showPayNowModal && ReactDOM.createPortal(
        <div
          onClick={closePayNowModal}
          style={{
            // z-index must beat DashboardHeader's floating pill nav (1001).
            // Portaled into <body> below so no ancestor stacking context
            // (TransactionDetailPage's layout container has transforms + its
            // own z-indexed children) can trap the modal below the nav.
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(27, 10, 60, 0.55)',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            padding: '40px 20px', overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12,
              width: '100%', maxWidth: 560,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '1px solid #ede8f8',
            }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#2b1055' }}>
                {checkoutClientSecret ? 'Complete payment' : 'Process Payment'}
              </h3>
              <button
                type="button"
                onClick={closePayNowModal}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 22, color: '#7a6f99', padding: '4px 8px', borderRadius: 6,
                }}
                aria-label="Close"
              >×</button>
            </div>

            {checkoutClientSecret ? (
              <div style={{ padding: 0 }}>
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{
                    clientSecret: checkoutClientSecret,
                    onComplete: handleCheckoutComplete,
                  }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <div style={{ padding: '20px 24px' }}>
                {isBannerPaying ? (
                  <p style={{ margin: 0, color: '#4a3f6b', fontSize: 14 }}>
                    Starting checkout…
                  </p>
                ) : checkoutError ? (
                  <>
                    <p style={{ margin: 0, color: '#991b1b', fontSize: 14 }}>
                      {checkoutError}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                      <button
                        type="button"
                        onClick={closePayNowModal}
                        style={{
                          padding: '8px 16px', border: '1px solid #c6c0d6',
                          background: '#fff', color: '#4a3f6b', borderRadius: 6,
                          fontWeight: 600, cursor: 'pointer',
                        }}
                      >Close</button>
                      <button
                        type="button"
                        onClick={handleBannerPayNow}
                        style={{
                          padding: '8px 16px', border: 'none',
                          background: '#2D1B69', color: '#fff', borderRadius: 6,
                          fontWeight: 600, cursor: 'pointer',
                        }}
                      >Try again</button>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* Lightweight toast for banner Pay Now feedback — fixed bottom-center. */}
      {bannerToast && (
        <div style={{
          position: 'fixed', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: '#1E293B', color: '#fff',
          padding: '10px 18px', borderRadius: '8px',
          fontSize: '14px', fontWeight: 500,
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          zIndex: 1000,
        }}>
          {bannerToast}
        </div>
      )}
    </div>
  );
};

export default TransactionDetailPage;
